export const GENERATION_IMPACT_SCHEMA_VERSION = 1 as const;
export const GENERATION_IMPACT_MODEL_VERSION = 'generation-impact-v1' as const;
export const GENERATION_IMPACT_HASH_VERSION = 'json-stringify-sha256-v1' as const;
export const GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION = 1 as const;
export const GENERATION_IMPACT_SED_CONTRACT_VERSION = 2 as const;
export const GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION = 1 as const;
export const GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION = 1 as const;
export const GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION = 'generation-layout-exhibit-v1' as const;
export const GENERATION_LAYOUT_EXHIBIT_EXPORT_ID = 'generation-layout-exhibit' as const;
export const GENERATION_SIZING_STUDY_SCHEMA_VERSION = 1 as const;
export const GENERATION_SIZING_STUDY_MODEL_VERSION = 'uniform-generation-alternatives-v1' as const;
/** Max-power envelope study — versioned INDEPENDENTLY of the sizing study and of
 *  the exhibit, so each can evolve without invalidating the others. */
export const GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION = 1 as const;
export const GENERATION_MAX_POWER_STUDY_MODEL_VERSION = 'max-power-envelope-v1' as const;
export const GENERATION_EQUIPMENT_CATALOG_VERSION = 'generation-equipment-catalog-v1' as const;
export const GENERATION_EQUIPMENT_DUTY_HASH_VERSION = 'canonical-json-sha256-v1' as const;
export const GENERATION_PROJECT_DUTY_SCHEMA_VERSION = 1 as const;
export const GENERATION_PROJECT_DUTY_MODEL_VERSION = 'generation-project-duty-v1' as const;
export const GENERATION_PROJECT_DUTY_HASH_VERSION = 'canonical-json-sha256-v1' as const;
export const GENERATION_BOM_BASIS_SCHEMA_VERSION = 1 as const;
export const GENERATION_BOM_BASIS_MODEL_VERSION = 'generation-bom-basis-v1' as const;
export const GENERATION_BOM_BASIS_HASH_VERSION = 'canonical-json-sha256-v1' as const;

/**
 * Screening-grade generation noise input. The discriminant prevents a
 * measured/reference sound-pressure level from being treated as sound power.
 */
export type NoiseDatum =
  | {
      kind: 'sound-power';
      /** A-weighted sound-power level for one unit, dBA re 1 pW. */
      levelDbA: number;
      directivityCorrectionDb?: number;
    }
  | {
      kind: 'reference-spl';
      /** A-weighted free-field sound-pressure level at referenceDistanceM. */
      levelDbA: number;
      referenceDistanceM: number;
      /** Aggregate ignores equipment count; per-unit adds acoustic energy. */
      scope: 'aggregate' | 'per-unit';
      basisLabel?: string;
    };

export type GenerationTechnology =
  | 'reciprocating-engine'
  | 'simple-cycle-gas-turbine'
  | 'combined-cycle-block'
  | 'grid-import'
  | 'battery-storage'
  | 'solar-bess'
  | 'wind-bess'
  | 'nuclear-smr'
  | 'large-nuclear';

export type GenerationEquipmentRatingScope =
  | 'net-plant'
  | 'gross-plant'
  | 'generator-terminals';

export type GenerationEquipmentSourceKind =
  | 'oem-rating'
  | 'project-precedent'
  | 'derived-aggregation';

export type GenerationEquipmentDataStatus =
  | 'published'
  | 'published-family-only'
  | 'planning-assumption'
  | 'oem-data-required'
  | 'conflicting-oem-sources';

export type GenerationCoolingSystem =
  | 'air-cooled-condenser'
  | 'closed-loop-wet'
  | 'closed-loop-radiator'
  | 'simple-cycle-air-cooled'
  | 'project-specific';

/** One allowlisted public technical source retained with the selected equipment basis. */
export interface GenerationEquipmentPublishedSource {
  publisher: string;
  title: string;
  url: string;
  retrievedAt: string;
  revision?: string;
  publishedAt?: string;
}

/**
 * Per-block acoustic source figure carried by a catalog entry.
 * `basis` is the honesty discriminator: 'screening-reference' values are
 * industry design-basis defaults (cited in `sourceNote`) that the UI must
 * label "screening default — not an OEM guarantee"; 'oem-guarantee' requires
 * a per-model vendor citation. `dataStatus.acousticSourceData` stays
 * 'oem-data-required' until an OEM guarantee exists.
 */
export interface GenerationEquipmentPublishedAcoustics {
  basis: 'screening-reference' | 'oem-guarantee';
  /** Per-generation-block datum consumed by the acoustic kernel. */
  datum: NoiseDatum;
  operatingCase: string;
  measurementStandard?: string;
  /** Citation for the figure; vendor documents also live in publishedSources. */
  sourceNote: string;
}

/**
 * Hash-bound equipment provenance selected in the workbench. Published facts
 * remain distinct from the screening normalization used by the canonical
 * gross-output solver. Unknown OEM facts are null, never zero-filled.
 */
export interface GenerationEquipmentBasis {
  catalogVersion: typeof GENERATION_EQUIPMENT_CATALOG_VERSION;
  dutyHashVersion: typeof GENERATION_EQUIPMENT_DUTY_HASH_VERSION;
  catalogId: string;
  manufacturer: string;
  model: string;
  trainConfiguration: string;
  technology: GenerationTechnology;
  sourceKind: GenerationEquipmentSourceKind;
  confirmationRequired: boolean;
  gasEngineOrTurbineCountPerBlock: number;
  contingencyElement: 'combined-cycle-block' | 'gas-turbine' | 'engine-genset' | 'electrical-island';
  commonModeBoundary: string;
  publishedRating: {
    outputMWPerBlock: number;
    ratingScope: GenerationEquipmentRatingScope;
    referenceConditions: string;
    heatRateValue: number | null;
    heatRateUnit: 'Btu/kWh' | 'kJ/kWh' | null;
    heatingValueBasis: 'LHV' | 'HHV' | null;
    efficiencyPct: number | null;
    projectPrecedentMW: number | null;
  };
  /** Published package/transport reference only; never used as installed layout geometry. */
  publishedPhysicalReference: {
    scope: 'gas-turbine-package' | 'generating-set-transport-envelope';
    lengthM: number;
    widthM: number;
    heightM: number;
    massTonnes: number | null;
    sourceTitle: string;
    limitation: string;
  } | null;
  /**
   * Optional per-block acoustic figure (screening default or OEM guarantee).
   * Absent on bases saved before the acoustics generation — validation and
   * duty fingerprints key off THIS saved field, never the live catalog, so
   * older applied bases stay valid and 'aligned'.
   */
  publishedAcoustics?: GenerationEquipmentPublishedAcoustics;
  screeningNormalization: {
    appliedBlockCount: number;
    solverIsoGrossMWPerBlock: number;
    ambientFactor: number;
    degradationFactor: number;
    auxiliaryFractionOfIsoGross: number;
    solverHeatRateBtuPerKWhHhv: number;
    /** Null until a project/OEM availability basis is supplied. */
    forcedOutageRate: number | null;
    generationWithdrawalGalPerMWhGross: number;
    generationConsumptionGalPerMWhGross: number;
    plantAcresPerGrossMW: number;
    /** Existing solver convention; not an installed-nameplate footprint basis. */
    footprintPowerBasis: 'site-rated-gross';
    coolingAcres: number;
    switchyardAcres: number;
    coolingSystem: GenerationCoolingSystem;
    naturalGasLhvToHhvRatio: number | null;
    normalizationNote: string;
  };
  dataStatus: {
    ambientCorrectionCurve: GenerationEquipmentDataStatus;
    coolingHeatBalance: GenerationEquipmentDataStatus;
    fuelGasPressure: GenerationEquipmentDataStatus;
    acousticSourceData: GenerationEquipmentDataStatus;
    installedPlantFootprint: GenerationEquipmentDataStatus;
    ccsIntegration: GenerationEquipmentDataStatus;
    reliabilityAndMaintenance: GenerationEquipmentDataStatus;
  };
  unavailableFields: string[];
  publishedSources: GenerationEquipmentPublishedSource[];
  appliedDutyFingerprint: string;
}

/** Deterministic exhibit projection of the scenario-level equipment authority. */
export interface GenerationEquipmentExhibitBasis {
  selection: GenerationEquipmentBasis;
  currentDutyFingerprint: string;
  alignmentStatus: 'aligned' | 'modified';
  alignmentNotes: string[];
}

/**
 * Where a user equipment entry was imported from. Metadata only — NEVER an
 * authority claim; the entry stays user-tier/unverified regardless of source.
 */
export interface GenerationUserEquipmentImportSource {
  kind: 'equipment-bom' | 'oem-register' | 'equipment-catalog';
  bomItemId?: string;
  oemProductId?: string;
  oemPerformanceCaseId?: string;
  catalogEntryId?: string;
  /** Human-readable origin, e.g. 'Equipment BOM · "2.0 MW gas gensets" (qty 12)'. */
  label: string;
}

/**
 * One user-supplied, UNVERIFIED equipment entry. Unlike the shipped catalog it
 * carries no published sources, no OEM claim, and never becomes a
 * `GenerationEquipmentBasis`: applying it writes solver duty through
 * user-sourced assumptions only, so exhibits and reports cannot present it as
 * vendor-published data.
 */
export interface GenerationUserEquipmentEntry {
  id: string;
  manufacturer: string;
  model: string;
  trainConfiguration: string;
  /** Gas technologies only — the heat-rate normalization has no meaning for the others. */
  technology: Extract<GenerationTechnology,
    'reciprocating-engine' | 'simple-cycle-gas-turbine' | 'combined-cycle-block'>;
  unitsPerBlock: number;
  ratedOutputMWPerBlock: number;
  ratingScope: GenerationEquipmentRatingScope;
  heatRateValue: number | null;
  heatRateUnit: 'Btu/kWh' | 'kJ/kWh' | null;
  heatingValueBasis: 'LHV' | 'HHV' | null;
  /** Fraction of ISO gross (0.04 = 4%), mirroring the catalog planning convention. */
  auxiliaryFractionOfIsoGross: number;
  ambientFactor: number;
  degradationFactor: number;
  notes?: string;
  /** Optional known OEM contact for the data-request email; never auto-sent. */
  oemContactEmail?: string;
  /** User nominated this entry for platform source review (promotion path). */
  verificationRequested?: boolean;
  /** Optional import provenance (BOM line, OEM register, or catalog entry). */
  importSource?: GenerationUserEquipmentImportSource;
  createdAt: string;
}

/**
 * User-curated equipment selection persisted with the scenario. The shipped
 * catalog entries are SUGGESTIONS; only ids the user shortlisted plus their own
 * entries form the working equipment set. Optional for saved-scenario
 * compatibility — absent means "nothing picked yet".
 */
export interface GenerationEquipmentWorkspace {
  shortlistedCatalogIds: string[];
  userEquipment: GenerationUserEquipmentEntry[];
  /**
   * The user entry currently applied as the working duty, when any. Workspace
   * metadata only — never an authority claim; readers must re-verify that the
   * scenario units still match a re-application of the entry.
   */
  appliedUserEquipmentId?: string;
}

export type GenerationProjectDutyAuthorityRole =
  | 'oem'
  | 'epc'
  | 'owner-engineer'
  | 'utility'
  | 'acoustic-consultant'
  | 'water-provider'
  | 'permit-study';

interface GenerationProjectDutySourceBase {
  id: string;
  authorityRole: GenerationProjectDutyAuthorityRole;
  publisher: string;
  title: string;
  revision?: string;
  issuedAt?: string;
}

/**
 * One bounded project-duty source. Private documents retain only an inert file
 * name and full digest; storage paths, upload handles and signed URLs have no
 * representation in the scenario contract.
 */
export type GenerationProjectDutySource = GenerationProjectDutySourceBase & (
  | {
      disclosure: 'public';
      url: string;
      retrievedAt: string;
    }
  | {
      disclosure: 'private';
      fileName: string;
      sha256: string;
      /** Canonical Documents-tab record; the digest remains the byte authority. */
      siteDocumentId?: string;
    }
);

export interface GenerationAmbientCorrectionPoint {
  dryBulbC: number;
  grossOutputFactor: number;
}

export interface GenerationProjectPerformanceDuty {
  sourceIds: string[];
  /** Project-normalized solver rating; omitted when the catalog rating is retained. */
  referenceIsoGrossMWPerBlock?: number;
  ambientCorrection?: {
    input: 'dry-bulb-c';
    output: 'gross-output-factor';
    interpolation: 'piecewise-linear-no-extrapolation';
    designDryBulbC: number;
    points: GenerationAmbientCorrectionPoint[];
    /** Deterministically interpolated from points at designDryBulbC. */
    appliedGrossOutputFactor: number;
  };
  degradationFactor?: number;
  auxiliaryLoadMWPerBlock?: number;
  heatRateBtuPerKWhHhv?: number;
  forcedOutageRate?: number;
  referenceConditions: {
    elevationM?: number;
    relativeHumidityPct?: number;
    barometricPressureKPaAbsolute?: number;
    notes: string;
  };
  normalizationNote: string;
}

export interface GenerationProjectPressureDatum {
  valueKPa: number;
  reference: 'gauge' | 'absolute';
}

/**
 * Interconnect delivery capability as documented by the transporter.
 *
 * Present only when EVERY field is documented — the pressure-derate screen is
 * not computable from a subset, so a partial record must not be representable.
 * Absent on every dossier written before this type existed, which is what keeps
 * the frozen-exhibit re-derivation byte-identical (see
 * `generationProjectDuty.legacyBytes.test.ts`).
 */
export interface GenerationProjectFuelGasSupplyDuty {
  /**
   * How the quantity is established. ONLY 'firm-transport-agreement' may write
   * `availableGasCapacityDthPerDay`, which the solver and equipment ranking
   * both treat as contractually firm. A ±30% ROM / scoping estimate is
   * 'indicative': it is recorded, cited and derated, but never promoted to
   * firm — the `gas-capacity-mismatch` amber keeps firing against it.
   */
  quantityBasis: 'firm-transport-agreement' | 'indicative';
  /**
   * 'standard-volume' carries the documented HHV that legalizes the
   * MMscf/day -> Dth/day conversion. Without a documented HHV that conversion
   * is an assumption, which is why `convertFirmGasCapacityToDthPerDay` refuses
   * volumetric units on the automatic prefill path.
   */
  capacityBasis:
    | { kind: 'energy' }
    | {
        kind: 'standard-volume';
        /** Higher heating value, Btu per standard cubic foot. */
        documentedHhvBtuPerScf: number;
        /**
         * Base conditions that define the standard cubic foot. 14.65 / 14.696
         * / 14.73 psia are all in use — a 0.5% spread on the capacity — so the
         * basis is recorded and narrated rather than assumed.
         */
        standardConditions: {
          basePressureKPaAbsolute: number;
          baseTemperatureC: number;
        };
      };
  /** Dth/day when `capacityBasis.kind` is 'energy', else MMscf/day. */
  stationDesignCapacity: number;
  /**
   * Contractual maximum daily quantity, same units as `stationDesignCapacity`.
   * Kept separate because physics derates and contracts do not. Omitted when
   * no transportation agreement exists.
   */
  contractMaxDailyQuantity?: number;
  /** The pressure at which `stationDesignCapacity` was rated. */
  ratedAtPressure: GenerationProjectPressureDatum;
  /**
   * Single-member union by design (mirrors `ambientCorrection.interpolation`).
   * Standard-volume throughput scales with absolute pressure ONLY for a
   * velocity-limited meter run; a station limited by regulation or compression
   * derates differently and must be entered already derated. Validation
   * rejects any other value rather than silently applying the wrong physics.
   */
  capacityLimit:
    /**
     * Throughput scales with absolute pressure — the derate applies the
     * documented pressure ratio.
     */
    | 'velocity-limited-meter-run'
    /**
     * The station is limited by regulation, compression or another mechanism
     * that does NOT follow absolute-pressure scaling, and the quoted figure is
     * already stated at the site's minimum delivery pressure.
     *
     * The validator previously accepted only the velocity-limited case and told
     * the user a different station "must be documented already derated" — while
     * giving them no way to say so, which made a regulation-limited interconnect
     * unrepresentable rather than merely strict. Declaring this applies NO
     * pressure scaling: the platform will not invent a derate model it has no
     * documented basis for, and takes the figure as given.
     */
    | 'documented-derated-at-site-minimum';
  /**
   * Real-gas compressibility at the two operating points. Optional: absent
   * means the screen runs ideal-gas and carries an explicit uncertainty band.
   * Not cosmetic — Z can flip which limit binds (see `gasInterconnectDerate`).
   */
  compressibility?: {
    atRatedPressure: number;
    atMinimumDeliveryPressure: number;
  };
  /** Operating condition the rating applies to, e.g. 'Design day, 60 degF flowing'. */
  operatingCase: string;
  sourceIds: string[];
}

export interface GenerationProjectFuelGasDuty {
  sourceIds: string[];
  minimumRequired: GenerationProjectPressureDatum;
  maximumAllowed?: GenerationProjectPressureDatum;
  documentedSiteSupplyMinimum?: GenerationProjectPressureDatum;
  /**
   * The transporter's maximum operating pressure at the custody point.
   *
   * Without it the pressure screen measures upper headroom from the site
   * MINIMUM, which cannot establish an over-pressure condition at the equipment
   * inlet — the check says so in its own caveat. When present, the upper bound
   * is measured from this value instead, so a line maximum above the equipment
   * maximum reads as a `fail` rather than being invisible.
   */
  documentedSiteSupplyMaximum?: GenerationProjectPressureDatum;
  supply?: GenerationProjectFuelGasSupplyDuty;
}

export interface GenerationProjectAcousticSource {
  id: string;
  label: string;
  quantity: {
    scope: 'site-total' | 'per-generation-block';
    count: number;
  };
  operatingCase: string;
  appliedDatum: NoiseDatum;
  sourceIds: string[];
}

export interface GenerationProjectReservationEnvelope {
  lengthM: number;
  widthM: number;
}

/** Coordinate-free installed and route reservation dimensions. */
export interface GenerationProjectInstalledReservations {
  blockQuantity: number;
  generationBlockEnvelope: GenerationProjectReservationEnvelope & {
    scope: 'installed-operating-envelope';
    maintenanceClearanceIncluded: boolean;
    fireAccessIncluded: boolean;
  };
  coolingEnvelope?: GenerationProjectReservationEnvelope;
  switchyardEnvelope?: GenerationProjectReservationEnvelope;
  ccsCaptureEnvelope?: GenerationProjectReservationEnvelope;
  ccsCompressionEnvelope?: GenerationProjectReservationEnvelope;
  naturalGasRouteEnvelope?: GenerationProjectReservationEnvelope;
  co2ExportRouteEnvelope?: GenerationProjectReservationEnvelope;
  constructionLaydownEnvelope?: GenerationProjectReservationEnvelope;
  sourceIds: string[];
}

export type GenerationProjectWaterDuty = (
  | {
      kind: 'gross-output-intensity';
      withdrawalGalPerMWhGross: number;
      consumptionGalPerMWhGross: number;
    }
  | {
      kind: 'absolute-design-day';
      withdrawalGalPerDay: number;
      consumptionGalPerDay: number;
      referenceGrossMWhPerDay: number;
    }
) & {
  operatingCase: string;
  sourceIds: string[];
};

/**
 * Hash-bound project-specific OEM/EPC dossier. This authority overlays—but
 * never mutates—the immutable published catalog facts retained separately in
 * `equipmentBasis`.
 */
export interface GenerationProjectDutyBasis {
  schemaVersion: typeof GENERATION_PROJECT_DUTY_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_PROJECT_DUTY_MODEL_VERSION;
  hashVersion: typeof GENERATION_PROJECT_DUTY_HASH_VERSION;
  basisId: string;
  basisName: string;
  /**
   * Present when the dossier is bound to an immutable CATALOG envelope. Exactly
   * one of `catalogBinding` / `userEquipmentBinding` is set.
   *
   * Deliberately still OPTIONAL-at-the-type rather than versioned: a catalog
   * dossier writes byte-identical JSON to every dossier ever persisted, so the
   * replay locks in `generationProjectDuty.legacyBytes.test.ts` still hold and
   * no migration is owed. The binding kind is discriminated STRUCTURALLY, by
   * which key is present, and `canonicalJson` drops the absent one entirely.
   */
  catalogBinding?: {
    catalogId: string;
    catalogAppliedDutyFingerprint: string;
  };
  /**
   * Present when the dossier is bound to a USER-SUPPLIED equipment entry.
   *
   * The machine a project is actually buying — a BOM line with a quote against
   * it — arrives as a user entry, not a catalog row, and used to be the one item
   * that could not carry its own vendor's project data. It can now, because
   * `expectedUserEquipmentDutyFingerprint` gives the entry the immutable
   * fingerprint the dossier's restoration contract requires.
   *
   * The evidence tier is unchanged by this: a user entry stays unverified, and
   * a dossier bound to one inherits that standing rather than laundering it.
   */
  userEquipmentBinding?: {
    entryId: string;
    appliedDutyFingerprint: string;
  };
  /** Exact pre-dossier CCS acreage restored when the dossier is cleared/replaced. */
  restorationBaseline: {
    ccsCaptureFootprintAcres: number;
    ccsCompressionFootprintAcres: number;
    /**
     * Pre-dossier firm gas capacity, restored when the dossier is cleared or
     * replaced. `null` records "was undocumented before this dossier" so the
     * restore can DELETE the field rather than zero it; `undefined` (the key
     * absent) means this dossier never wrote it, which is the shape every
     * pre-existing dossier has and which `canonicalJson` drops entirely.
     */
    availableGasCapacityDthPerDay?: number | null;
  };
  performance?: GenerationProjectPerformanceDuty;
  installedReservations: GenerationProjectInstalledReservations;
  fuelGas?: GenerationProjectFuelGasDuty;
  acoustics?: {
    acousticSources: GenerationProjectAcousticSource[];
  };
  water?: GenerationProjectWaterDuty;
  sources: GenerationProjectDutySource[];
  /** Canonical hash of the dossier excluding only this field; binds appliedDutyFingerprint. */
  basisHash: string;
  /** Canonical hash of only the scenario fields applied by this dossier. */
  appliedDutyFingerprint: string;
}

export type GenerationProjectDutyEvidenceStatus =
  | 'supplied'
  | 'partial'
  | 'missing'
  | 'not-applicable';

export interface GenerationProjectDutyCompleteness {
  statuses: {
    ambientPerformance: GenerationProjectDutyEvidenceStatus;
    fuelGasPressure: GenerationProjectDutyEvidenceStatus;
    acousticSource: GenerationProjectDutyEvidenceStatus;
    waterBalance: GenerationProjectDutyEvidenceStatus;
    installedPlant: GenerationProjectDutyEvidenceStatus;
    naturalGasRoute: GenerationProjectDutyEvidenceStatus;
    ccsIntegration: GenerationProjectDutyEvidenceStatus;
    reliability: GenerationProjectDutyEvidenceStatus;
  };
  suppliedCount: number;
  totalCount: number;
  missingItems: string[];
}

export interface GenerationProjectDutySpatialSummary {
  permanentEquipmentAcres: number;
  routeReservationAcres: number;
  constructionLaydownAcres: number;
  totalReservedAcres: number;
}

export interface GenerationProjectDutyExhibitBasis {
  basis: GenerationProjectDutyBasis;
  currentDutyFingerprint: string;
  alignmentStatus: 'aligned' | 'modified';
  alignmentNotes: string[];
  completeness: GenerationProjectDutyCompleteness;
  gasPressureCheck: {
    verdict: EngineeringVerdict;
    /** Positive means documented site minimum is above the equipment minimum. */
    marginKPa: number | null;
    /**
     * Screening headroom between the equipment maximum and documented site
     * minimum. This does not establish the site's actual maximum pressure.
     */
    upperMarginKPa: number | null;
    caveat: string;
  };
  spatialSummary: GenerationProjectDutySpatialSummary;
}

/** Private project-document identity is deliberately absent from this ledger. */
export type PublicGenerationProjectWaterDuty = GenerationProjectWaterDuty extends infer Duty
  ? Duty extends { sourceIds: string[] }
    ? Omit<Duty, 'sourceIds'>
    : never
  : never;

export interface PublicGenerationProjectDutyExhibitBasis {
  basisName: string;
  catalogId: string;
  basisHash: string;
  appliedDutyFingerprint: string;
  currentDutyFingerprint: string;
  alignmentStatus: GenerationProjectDutyExhibitBasis['alignmentStatus'];
  alignmentNotes: string[];
  performance?: Omit<GenerationProjectPerformanceDuty, 'sourceIds'>;
  installedReservations: Omit<GenerationProjectInstalledReservations, 'sourceIds'>;
  /**
   * `Omit` is SHALLOW, so a nested `supply.sourceIds` would be typed as
   * belonging in the public ledger and could reach reports and the AI
   * narrative. The nested strip is explicit for that reason — and it makes
   * `projectPublicGenerationProjectDutyExhibitBasis` fail to compile until its
   * allowlist is extended, rather than silently dropping the sub-object.
   */
  fuelGas?: Omit<GenerationProjectFuelGasDuty, 'sourceIds' | 'supply'> & {
    supply?: Omit<GenerationProjectFuelGasSupplyDuty, 'sourceIds'>;
  };
  acoustics?: {
    acousticSources: Array<Omit<GenerationProjectAcousticSource, 'sourceIds'>>;
  };
  water?: PublicGenerationProjectWaterDuty;
  publicSources: Array<{
    authorityRole: GenerationProjectDutyAuthorityRole;
    publisher: string;
    title: string;
    revision?: string;
    url: string;
    retrievedAt: string;
  }>;
  privateSourceCount: number;
  completeness: GenerationProjectDutyCompleteness;
  gasPressureCheck: GenerationProjectDutyExhibitBasis['gasPressureCheck'];
  spatialSummary: GenerationProjectDutySpatialSummary;
}

export type AssumptionSource =
  | 'site-evidence'
  | 'document'
  | 'dam'
  | 'project-intent'
  | 'vendor'
  | 'user'
  | 'planning-default';

/**
 * Structured source lineage copied into a frozen scenario assumption.
 * Every member is optional at the scenario level for backward compatibility,
 * but document references are always bound to both the document id and SHA-256.
 */
export type GenerationAssumptionEvidenceRef =
  | {
      kind: 'document';
      documentId: string;
      documentSha256: string;
      extractionVersion: string;
      factId: string;
      page?: number;
      excerpt?: string;
      sourceOffset?: { start: number; end: number };
    }
  | {
      kind: 'sed';
      sedId?: string;
      section: string;
      fieldPath: string;
      sourceId?: string;
      sourceVersion?: string;
    }
  | {
      kind: 'dam';
      reportType: string;
      reportVersion: string;
      benchmarkId: string;
      basis?: string;
      vintage?: string;
      conversionFormula?: string;
    };

export interface GenerationAssumption {
  field: string;
  value: number | string | boolean;
  source: AssumptionSource;
  label: string;
  citation?: string;
  confidence?: 'low' | 'medium' | 'high';
  /** Frozen field-level lineage; absent on historical scenarios. */
  evidenceRefs?: GenerationAssumptionEvidenceRef[];
  /** True only while the workbench still owns this value as an automatic seed. */
  autoPopulated?: boolean;
}

export const GENERATION_SCREENING_EXTRACTION_SCHEMA_VERSION = 1 as const;
export const GENERATION_SCREENING_EXTRACTOR_VERSION = 'generation-screening-document-v3-source-qualified' as const;

/**
 * Canonical destinations accepted by the document screening validator.
 * Project-duty destinations are review-only until the existing duty-board
 * verification workflow grants them authority.
 */
export type GenerationScreeningEvidenceField =
  | 'availableGasCapacityDthPerDay'
  | 'availableWaterConsumptionGalPerDay'
  | 'ccsExportCapacityTonnesPerYear'
  | 'water.dataCenterConsumptionGalPerMWhFacility'
  | 'receptors.distanceM'
  | 'lighting.fixtureCount'
  | 'lighting.wattsPerFixture'
  | 'lighting.lumensPerFixture'
  | 'lighting.fullyShielded'
  | 'lighting.bugRated'
  | 'lighting.motionOrCurfewControls'
  | 'lighting.iesPhotometricAvailable'
  | 'projectDuty.referenceIsoGrossMWPerBlock'
  | 'projectDuty.ambientCorrection.designDryBulbC'
  | 'projectDuty.ambientCorrection.point.dryBulbC'
  | 'projectDuty.ambientCorrection.point.grossOutputFactor'
  | 'projectDuty.auxiliaryLoadMWPerBlock'
  | 'projectDuty.heatRateBtuPerKWhHhv'
  | 'projectDuty.water.withdrawalGalPerMWhGross'
  | 'projectDuty.water.consumptionGalPerMWhGross'
  | 'projectDuty.installedEnvelope.lengthM'
  | 'projectDuty.installedEnvelope.widthM'
  | 'projectDuty.ccs.specificPowerKWhPerTonneCaptured'
  | 'projectDuty.ccs.waterWithdrawalGalPerTonneCaptured'
  | 'projectDuty.ccs.waterConsumptionGalPerTonneCaptured'
  | 'projectDuty.acoustics.soundPowerDbA'
  | 'projectDuty.acoustics.referenceSplDbA'
  | 'projectDuty.acoustics.referenceDistanceM';

export type GenerationScreeningCanonicalUnit =
  | 'Dth/day'
  | 'gal/day'
  | 'metric-tonne/year'
  | 'gal/MWh-facility'
  | 'm'
  | 'MW'
  | 'degC'
  | 'fraction'
  | 'Btu/kWh-HHV'
  | 'gal/MWh-gross'
  | 'kWh/metric-tonne'
  | 'gal/metric-tonne'
  | 'dBA-sound-power'
  | 'dBA-reference-SPL'
  | 'count'
  | 'W'
  | 'lm'
  | 'boolean';

export type GenerationScreeningFactStatus =
  | 'eligible'
  | 'project-duty-review'
  | 'review-only'
  | 'rejected';

export type GenerationScreeningFactRejectionReason =
  | 'hash-missing'
  | 'hash-stale'
  | 'document-excluded'
  | 'unreadable-document'
  | 'field-unsupported'
  | 'value-invalid'
  | 'unit-unsupported'
  | 'basis-ambiguous'
  | 'range-without-design-value'
  | 'technology-mismatch'
  | 'not-firm-site-deliverable'
  | 'withdrawal-is-not-consumption'
  | 'hhv-basis-required'
  | 'sound-power-basis-required'
  | 'installed-design-not-established'
  | 'owner-model-assumption'
  | 'excerpt-unverified'
  | 'page-unverified';

export interface GenerationScreeningDocumentFact {
  /** Stable within one extraction: `${field}:${sourceOffset.start}:${index}`. */
  id: string;
  field: GenerationScreeningEvidenceField | 'unsupported';
  /** Original model-emitted path when it was not in the allowlist. */
  rawField?: string;
  target: 'screening-input' | 'project-duty-draft';
  /** Values/units exactly as identified in the document, never model-converted. */
  raw: {
    value: number | string | boolean;
    unit?: string;
    basis?: string;
    range?: { min: number; max: number; selectedDesignValue?: number };
  };
  /** Present only when deterministic validation and conversion succeeded. */
  canonical?: {
    value: number | boolean;
    unit: GenerationScreeningCanonicalUnit;
    basis?: string;
    conversionFormula?: string;
  };
  applicability: {
    technologies?: GenerationTechnology[];
    coolingSystems?: GenerationCoolingSystem[];
    loadBasis?: 'facility' | 'it-load';
    equipmentModels?: string[];
    groupId?: string;
  };
  qualification: {
    firm?: boolean;
    siteDeliverable?: boolean;
    consumption?: boolean;
    withdrawal?: boolean;
    heatRateBasis?: 'HHV' | 'LHV' | 'unspecified';
    acousticKind?: 'sound-power' | 'reference-spl' | 'unspecified';
    installedDesign?: boolean;
    pue?: number;
  };
  /** 0..1 extraction confidence; explicit owner assumptions are capped low.
   * Confidence never overrides source qualification or deterministic validation. */
  confidence: number;
  status: GenerationScreeningFactStatus;
  rejectionReasons?: GenerationScreeningFactRejectionReason[];
  provenance: {
    documentSha256: string;
    page?: number;
    excerpt: string;
    sourceOffset?: { start: number; end: number };
  };
}

/** Versioned, hash-bound output persisted on SiteDocument. */
export interface GenerationScreeningDocumentExtraction {
  schemaVersion: typeof GENERATION_SCREENING_EXTRACTION_SCHEMA_VERSION;
  extractorVersion: typeof GENERATION_SCREENING_EXTRACTOR_VERSION;
  cacheKey: string;
  documentSha256?: string;
  extractedAt: string;
  status: 'complete' | 'review-only' | 'failed';
  facts: GenerationScreeningDocumentFact[];
  failureReason?: string;
}

/** Generation-workbench unit contract. */
export interface GenerationUnit {
  id: string;
  label: string;
  technology: GenerationTechnology;
  /** Units sharing this id are removed together for the largest-contingency test. */
  contingencyGroupId: string;
  count: number;
  isoGrossMWPerUnit: number;
  ambientFactor: number;
  degradationFactor: number;
  auxiliaryLoadMWPerUnit: number;
  heatRateBtuPerKWhHhv: number;
  forcedOutageRate?: number;
  /** Preferred acoustic input. */
  noise?: NoiseDatum;
  /**
   * Backward-compatible input. When `noise` is absent this is interpreted
   * exactly as one-unit A-weighted sound power using the legacy equation.
   */
  soundPowerDbA?: number;
  /** Legacy directivity correction paired with soundPowerDbA. */
  directivityCorrectionDb?: number;
  /** Source-specific attenuation explicitly supplied by the modeller. */
  barrierLossDb?: number;
  noxLbPerMWh?: number;
  coLbPerMWh?: number;
  /**
   * MIXED-FLEET intensities. Water (gal/MWh gross) and plant footprint
   * (acres/MW gross) are properties of the TECHNOLOGY, not of the site — a
   * combined-cycle block and a reciprocating-engine hall have very different
   * figures. When a fleet mixes technologies, each unit carries its own value
   * (seeded from its catalog entry) and the solver aggregates per unit:
   *
   *   water_gal/day = Σ (unit gross MW × 24 h × unit gal/MWh)
   *   plant acres   = Σ (unit gross MW × unit acres/MW)
   *
   * OMIT them for a single-technology fleet: the solver then uses the
   * plant-wide `input.water` / `input.footprint` intensities exactly as before,
   * so every existing scenario reproduces byte-identical results.
   */
  generationWithdrawalGalPerMWhGross?: number;
  generationConsumptionGalPerMWhGross?: number;
  plantAcresPerGrossMW?: number;
}

export interface CcsConfiguration {
  enabled: boolean;
  captureRate: number;
  specificPowerKWhPerTonneCaptured: number;
  waterWithdrawalGalPerTonneCaptured: number;
  waterConsumptionGalPerTonneCaptured: number;
  footprintAcres: number;
  compressionFootprintAcres: number;
  pipelineExportConfirmed: boolean;
}

/** Generation-workbench receptor contract. */
export interface NoiseReceptor {
  id: string;
  label: string;
  distanceM: number;
  barrierLossDb: number;
  thresholdDbA?: number;
  location?: { lat: number; lng: number };
}

/**
 * Frozen geometric context for the grid-screening noise model. Its PRESENCE
 * (together with `noiseSources`) is the v2 opt-in: the solver additionally
 * computes `result.noiseField`. Kept OUTSIDE the equipment-duty fingerprint
 * (which hashes `noiseSources`) so layout moves never churn
 * `appliedDutyFingerprint`. Everything the field depends on must live in
 * scenario input — the server re-solves from input alone and replays at
 * publish requiring hash equality.
 */
export interface GenerationNoiseGeometry {
  /** How positions were produced; 'manual' suppresses workbench auto-sync. */
  basis: 'layout-blocks' | 'anchor' | 'manual';
  sources: Array<{
    /** Joins input.noiseSources[].id. */
    noiseSourceId: string;
    /** Deterministic emitter positions; count/energy spreads evenly across them. */
    positions: Array<{ lat: number; lng: number }>;
    /** Acoustic-centre height above grade, metres; engine default 1.5. */
    heightM?: number;
  }>;
  /** Provenance only (drift badge); never read by the solver. */
  layoutFingerprint?: string;
}

export interface LightingConfiguration {
  fixtureCount?: number;
  wattsPerFixture?: number;
  lumensPerFixture?: number;
  fullyShielded?: boolean;
  bugRated?: boolean;
  motionOrCurfewControls?: boolean;
  iesPhotometricAvailable?: boolean;
}

export interface GenerationFootprintInput {
  plantAcresPerGrossMW: number;
  /** Absolute installed operating area; prevents physical land from scaling with ambient derate. */
  installedPlantAcres?: number;
  coolingAcres: number;
  switchyardAcres: number;
  /**
   * OPTIONAL heat-rejection area intensity, acres per SITE-RATED gross MW.
   *
   * When PRESENT it REPLACES the flat `coolingAcres` (the same precedence rule
   * `installedPlantAcres` has over `plantAcresPerGrossMW`) — replacement rather
   * than addition, because the flat figure already represents the whole area at
   * the scenario's current rating.
   *
   * OMIT to keep every existing scenario byte-identical: absent ⇒ the solver
   * takes the legacy constant path and pushes no extra gap, so `resultHash` and
   * the publish-gate replay are unchanged.
   *
   * CAVEAT: real heat-rejection area is a STEP function of cell/train count and
   * rejection technology, and is wet-bulb dependent. One acres/MW figure is a
   * first-order screening linearisation — results carrying
   * `coolingAreaBasis: 'gross-output-intensity'` must be labelled as such.
   */
  coolingAcresPerGrossMW?: number;
  /**
   * OPTIONAL switchyard area intensity, acres per site-rated gross MW. Same
   * presence-gated replacement contract as `coolingAcresPerGrossMW`. Real
   * switchyard area steps with bay / breaker count and voltage class.
   */
  switchyardAcresPerGrossMW?: number;
}

/** Allowlisted external-geometry lineage retained across import and export. */
export interface GenerationImportedGeometryRef {
  fileName: string;
  layerName?: string;
  sourceSha256?: string;
  entityHandle?: string;
  entityType?: string;
  dwgVersion?: string;
  metresPerUnit?: number;
  crsCode?: string;
}

/**
 * Links a derived layout artifact back to the imported-source assignment that
 * produced it, so the reconciler can remove every artifact a source owns —
 * across all collections — before re-creating them under a new role. This is
 * what prevents orphan geometry when an imported feature is reclassified.
 */
export interface GenerationImportedLayerOrigin {
  kind: 'imported-layer-assignment';
  assignmentId: string;
}

/**
 * Persisted record of one imported source's CURRENT Studio assignment, stored
 * scenario-locally in schema-v2 layout data. Source identity (`id` /
 * `sourceContentHash`) is role-INDEPENDENT, so reassigning a source updates
 * this record in place rather than duplicating it.
 */
export interface GenerationImportedLayerAssignment {
  /** Equals the derived artifacts' `origin.assignmentId` (role-independent). */
  id: string;
  sourceLabel: string;
  sourceContentHash: string;
  geometryType: 'point' | 'line' | 'polygon';
  /** The engineering role the source currently holds (never 'reference'). */
  role: 'exclusion' | 'barrier' | 'custom' | 'equipment';
  /** Equipment role only: the bound packer component id. */
  componentId?: string;
}

/** One no-build polygon subtracted from the buildable area before packing. */
export interface GenerationLayoutExclusionZone {
  id: string;
  label: string;
  source: 'fema-nfhl' | 'usfws-nwi' | 'derived-slope' | 'user-drawn' | 'site-layer';
  kind: 'flood-1pct' | 'flood-0.2pct' | 'floodway' | 'wetland' | 'steep-slope' | 'custom';
  /** Outer rings in lat/lng. Holes are deliberately dropped: excluding a dry
   * island inside a flood zone is conservative, never unsafe. */
  polygons: Array<Array<{ lat: number; lng: number }>>;
  /** Present only when the exact pairwise merge failed inside polygon-clipping
   * and a disclosed fallback tier produced this zone. */
  geometryTier?: 'single-sweep' | 'precision-snap';
  /** Present only with geometryTier 'precision-snap': the fixed coordinate
   * grid (degrees) the merge was recomputed on. */
  precisionSnapDeg?: number;
  provenance?: {
    providerId: string;
    retrievedAt: string;
    url?: string;
    effectiveDate?: string;
    contentHash?: string;
  };
  importRef?: GenerationImportedGeometryRef;
  /** Ownership link to an imported-source assignment (reconciler removal). */
  origin?: GenerationImportedLayerOrigin;
}

/**
 * Snapshot of the exclusion zones applied to a layout. `femaStatus` records
 * why zones may be empty so "no FEMA data" is never presented as "no flood
 * risk" — an unavailable query must surface as an explicit caveat.
 */
export interface GenerationLayoutExclusionSet {
  zones: GenerationLayoutExclusionZone[];
  femaStatus: 'applied' | 'partial' | 'unavailable' | 'not-fetched' | 'disabled';
  femaCoverage?: import('./floodScreening').FloodScreeningCoverage;
  femaUnavailableReason?: string;
  /** Positive zones may be retained even when the mapping screen is incomplete. */
  nwiStatus?: 'applied' | 'partial' | 'unavailable';
  nwiUnavailableReason?: string;
  nwiRetrievedAt?: string;
  nwiCoverage?: import('./refdata').NwiMappingCoverage;
  /** True when 0.2%-annual-chance (500-yr) zones were included in the fetch. */
  include500yr?: boolean;
  retrievedAt?: string;
}

/**
 * One user-drawn acoustic screen for the grid-screening noise model.
 * Wall/berm: an open polyline (≥2 vertices). Building: a closed outline
 * ring stored open (≥3 vertices); its edges all screen at `heightM`.
 * Height is user-entered and required — never defaulted (engineering rule:
 * no guessed values).
 */
export interface GenerationAcousticBarrier {
  id: string;
  label: string;
  kind: 'wall' | 'berm' | 'building';
  path: Array<{ lat: number; lng: number }>;
  /** Screen top height above local grade, metres. Valid 0.5–50. */
  heightM: number;
  createdAt?: string;
  importRef?: GenerationImportedGeometryRef;
  /** Ownership link to an imported-source assignment (reconciler removal). */
  origin?: GenerationImportedLayerOrigin;
}

/**
 * Test-Fit Studio pin (layout schemaVersion 2). Coordinates live in the
 * ROTATED, parcel-centred local metre plane — the same plane as
 * GenerationTestFitRectangle — so pinned equipment rotates WITH the plant
 * grid when layout.rotationDeg changes. Frozen scenario input: the server
 * re-solves from input alone and replays at publish requiring hash equality.
 */
export interface GenerationLayoutManualPlacement {
  /** Stable packer item id: 'generation-block-3', 'cooling', 'switchyard', … */
  componentId: string;
  /** Rectangle centre, local metres. */
  xM: number;
  yM: number;
  /** CCW rotation about the centre, relative to the layout axes. */
  rotationDeg: number;
  /** Provenance only — never read by the solver. */
  pinnedAt?: string;
  /** Ownership link to an imported-source assignment (reconciler removal). */
  origin?: GenerationImportedLayerOrigin;
}

export type GenerationCustomComponentKind =
  | 'road'
  | 'parking'
  | 'building'
  | 'data-center'
  | 'generator'
  | 'custom';

/**
 * Test-Fit Studio user-defined component (layout schemaVersion 2).
 * WORLD-frame geometry — it does NOT rotate with the plant grid (unlike
 * manual placements). Roads are centerline polylines with a corridor width
 * (CAD-native; the corridor polygon derives deterministically); other kinds
 * are rectangles (centre + world bearing) or drawn polygons. Always manually
 * placed, never packed; participates in containment, exclusion overlap and
 * takeoffs (geometry scope — no BOM costing).
 */
export interface GenerationCustomComponent {
  id: string;
  kind: GenerationCustomComponentKind;
  label: string;
  shape:
    | { type: 'rect'; widthM: number; heightM: number }
    | { type: 'polygon'; path: Array<{ lat: number; lng: number }> }
    | { type: 'polyline'; path: Array<{ lat: number; lng: number }>; widthM: number };
  /** Rect shapes only: centre + CCW bearing from geographic east. */
  position?: { lat: number; lng: number };
  rotationDeg?: number;
  lifecycle: 'permanent';
  notes?: string;
  source?: 'drawn' | 'imported';
  /** Allowlisted CAD lineage retained in schema-v2 exports and retries. */
  importRef?: GenerationImportedGeometryRef;
  /** Ownership link to an imported-source assignment (reconciler removal). */
  origin?: GenerationImportedLayerOrigin;
}

export type GenerationLayoutPackingScheme = 'compact' | 'road-rows' | 'auto';

export interface GenerationLayoutWindRoseSector {
  /** Sector centre, meteorological FROM bearing (deg CW from north). */
  fromDeg: number;
  frequencyPct: number;
  meanSpeedMps: number;
}

/**
 * Snapshotted prevailing-wind distribution for the layout. Inert to the
 * solver — it informs rotation choice (crosswind rows reduce hot-exhaust
 * recirculation between cooling/generator rows) and map/exhibit rendering.
 */
export interface GenerationLayoutWindRose {
  sectors: GenerationLayoutWindRoseSector[];
  prevailingFromDeg: number;
  calmPct: number;
  source: string;
  retrievedAt: string;
  periodStart: string;
  periodEnd: string;
}

/**
 * Reproducible planning-layout controls shared by the workbench preview and
 * the server-side Site Map publisher. Distances are stored in metres even
 * when the client presents feet to US project teams.
 */
export interface GenerationLayoutConfiguration {
  /** Stable SiteBoundaryParcel id. Omitted only when no parcel is available. */
  targetParcelId?: string;
  /** Counter-clockwise rotation of the layout x-axis from local east. */
  rotationDeg: number;
  orientation: 'auto' | 'blocks-along-width' | 'blocks-along-height';
  /** Uniform clear distance inside the oriented parcel envelope. */
  edgeSetbackM: number;
  /** Clear distance between deterministic planning rectangles. */
  componentGapM: number;
  /** Width-to-height planning ratio for each generation block. */
  generationBlockAspectRatio: number;
  /** No-build polygons subtracted from the buildable area before packing. */
  exclusions?: GenerationLayoutExclusionSet;
  /** Map-drawn alignment axis recorded as provenance for rotationDeg. */
  frontline?: { start: { lat: number; lng: number }; end: { lat: number; lng: number } };
  /** Prevailing-wind snapshot; provenance for wind-aligned rotation. */
  windRose?: GenerationLayoutWindRose;
  /** Access-road corridor width inserted between generation-block rows. */
  rowRoadWidthM?: number;
  /** Packing arrangement; 'auto' compares compact vs road-rows candidates. */
  packingScheme?: GenerationLayoutPackingScheme;
  /** User-drawn sound screens consumed by the grid-screening noise model. */
  acousticBarriers?: GenerationAcousticBarrier[];
  /**
   * Layout semantics version. ABSENT = legacy semantics preserved
   * byte-identical for frozen replay (including the historical road-gap
   * `max()` behaviour and v3 stamping). The Test-Fit Studio stamps 2 on
   * every patch it writes, enabling pin+pack, additive road corridors,
   * emitted access-road rectangles, and custom components.
   */
  schemaVersion?: 2;
  /** Studio pins — layout-frame manual position/rotation per packer item. */
  manualPlacements?: GenerationLayoutManualPlacement[];
  /** Studio user-defined components — world-frame, never packed. */
  customComponents?: GenerationCustomComponent[];
  /** Imported-source → Studio-role assignments (role-independent source ids). */
  importedAssignments?: GenerationImportedLayerAssignment[];
  /**
   * User-set display names for solver-generated components, keyed by component
   * id (e.g. `generation-block-1` → "North block"). Applied as a pure post-pass
   * over the solved result, so a rename reaches EVERY consumer — canvas, layer
   * tree, inspector, exports, takeoffs and reports — while touching no geometry,
   * area or physics. Absent/blank entries fall back to the solver's own label.
   */
  componentLabels?: Record<string, string>;
}

export interface GenerationWaterInput {
  generationWithdrawalGalPerMWhGross: number;
  generationConsumptionGalPerMWhGross: number;
  /** Optional sourced design-day duty; when present the daily solver output remains absolute. */
  generationDesignDay?: {
    withdrawalGalPerDay: number;
    consumptionGalPerDay: number;
    referenceGrossMWhPerDay: number;
  };
  dataCenterWithdrawalGalPerMWhFacility?: number;
  dataCenterConsumptionGalPerMWhFacility?: number;
}

export type GenerationBomItemUnit = 'each' | 'kVA' | 'MW' | 'MWh' | 'm' | 'm2' | 'ton' | 'm3' | 'lot';

export type GenerationBomItemStatus =
  | 'planned'
  | 'rfq-out'
  | 'quoted'
  | 'ordered'
  | 'in-transit'
  | 'on-site'
  | 'commissioned';

/**
 * One frozen, allowlisted active generation BOM line. Capacity is deliberately
 * labelled net nameplate: it is procurement evidence and must never replace a
 * GenerationUnit.isoGrossMWPerUnit solver input.
 */
export interface GenerationBomItemBasis {
  bomItemId: string;
  itemHash: string;
  category: 'power-generation';
  name: string;
  spec: string;
  quantity: number;
  unit: GenerationBomItemUnit;
  status: GenerationBomItemStatus;
  phaseId: string | null;
  optionGroupId: string | null;
  selectionMethod: 'additive' | 'explicit' | 'lowest-grounded-cost' | 'first-unpriced';
  derivedFrom: 'auto-template' | 'manual' | 'uploaded-bom';
  netNameplateMWPerUnit: number | null;
  totalNetNameplateMW: number | null;
  unitCostUsd: number | null;
  totalCostUsd: number | null;
  priceSource: 'industry-baseline' | 'manual' | 'quote' | 'catalog';
  priceQuoteId: string | null;
  leadTimeMonthsLow: number;
  leadTimeMonthsHigh: number;
  leadTimeSource: 'industry-baseline' | 'manual' | 'quote' | 'unknown';
  requiredOnSiteDate: string | null;
  expectedOnSiteDate: string | null;
  awardedSupplierId: string | null;
  quoteIds: string[];
  evidenceDocumentIds: string[];
}

/**
 * Immutable Generation & Impact provenance link to the active generation BOM.
 * `solverBinding: 'none'` is a contract guard, not a missing implementation:
 * the BOM expresses net nameplate while the physics model requires ISO gross.
 */
export interface GenerationBomBasis {
  schemaVersion: typeof GENERATION_BOM_BASIS_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_BOM_BASIS_MODEL_VERSION;
  hashVersion: typeof GENERATION_BOM_BASIS_HASH_VERSION;
  sourcePlanVersion: number;
  sourcePlanUpdatedAt: string;
  sourcePlanHash: string;
  selectionMode: 'active-generation-items';
  capacityBasis: 'net-nameplate-reference-only';
  solverBinding: 'none';
  items: GenerationBomItemBasis[];
  totalNetNameplateMW: number | null;
  basisHash: string;
}

/**
 * An engineer's attested resolution of an OEM rating conflict.
 *
 * Some catalog entries ship `confirmationRequired: true` because the vendor
 * publishes irreconcilable figures for the same machine (Siemens SCC6-5000F:
 * 391 MW net, 398 MW gross, 387 MW overview). The platform refuses to guess,
 * because whichever value it picked would flow into duty, N-1, water and
 * footprint as though it were established fact.
 *
 * This is how that gets resolved WITHOUT weakening the gate: the user records
 * the reconciled figure together with the evidence for it. The catalog entry
 * stays `confirmationRequired` forever; the SCENARIO carries the attestation.
 * That keeps provenance answerable — who confirmed what, on what basis, when —
 * instead of an anonymous "use anyway" click.
 *
 * Frozen with the scenario input, so it replays with the run it justified.
 */
export interface GenerationEquipmentRatingConfirmation {
  /** Catalog entry this attestation unblocks. */
  catalogId: string;
  /** The reconciled figure the project will engineer against. */
  confirmedOutputMW: number;
  /** Which published basis the figure is on — net vs gross is the whole conflict. */
  confirmedRatingScope: GenerationEquipmentRatingScope;
  /**
   * Where the reconciliation came from. Free text by design: a vendor email
   * reference, quote number, or signed datasheet title. Required — an
   * attestation without a source is just an override.
   */
  sourceReference: string;
  /** Who attested it (name or email), for the audit trail. */
  confirmedBy: string;
  /** ISO timestamp of the attestation. */
  confirmedAt: string;
  /** Optional engineer's note (what was reconciled, and how). */
  note?: string;
}

export interface GenerationImpactScenarioInput {
  /** Exact user-selected study area used by this scenario, absent for legacy sites. */
  studyAreaSelectionHash?: string;
  schemaVersion: typeof GENERATION_IMPACT_SCHEMA_VERSION;
  name: string;
  siteId: string;
  targetPhaseId?: string;
  gridConnection: 'islanded' | 'grid-connected' | 'undecided';
  facilityLoadMW: number;
  criticalLoadMW?: number;
  reserveMarginPct?: number;
  /** Number of largest independent contingency groups the scenario must survive. 0 = N, 1 = N-1, 2 = N-2, etc. */
  contingencyOutages?: number;
  annualOperatingHours: number;
  availableGasCapacityDthPerDay?: number;
  availableWaterConsumptionGalPerDay?: number;
  ccsExportCapacityTonnesPerYear?: number;
  /**
   * Workflow-only record that a supply figure has been requested from the
   * counterparty (utility / water works / CO₂ offtaker) and the response is
   * pending. NEVER read by the solver — presence cannot change any result,
   * so stored scenarios that lack the key replay byte-identically (canonical
   * JSON drops absent optional fields). Keys are removed when the limit
   * becomes documented. Members must never hold `undefined` (Firestore).
   */
  resourceLimitRequests?: Partial<Record<'gas' | 'water' | 'co2', {
    /** ISO date the request was sent / marked. */
    requestedAt: string;
    counterparty?: string;
    note?: string;
  }>>;
  units: GenerationUnit[];
  /**
   * Explicit acoustic sources that are independent of the electrical unit
   * inventory. Use this for aggregate planning assumptions so equipment
   * count is never applied implicitly. When absent, the solver adapts the
   * acoustic fields on `units` for persisted-scenario compatibility.
   */
  noiseSources?: GenerationNoiseUnit[];
  /**
   * Frozen emitter positions/heights for the grid-screening noise model.
   * Presence (with `noiseSources`) opts the scenario into v2; absence keeps
   * every legacy path byte-identical.
   */
  noiseGeometry?: GenerationNoiseGeometry;
  ccs: CcsConfiguration;
  water: GenerationWaterInput;
  footprint: GenerationFootprintInput;
  receptors: NoiseReceptor[];
  lighting: LightingConfiguration;
  assumptions: GenerationAssumption[];
  /** Optional and added only after an explicit workbench equipment selection. */
  equipmentBasis?: GenerationEquipmentBasis;
  /**
   * Attested resolutions of OEM rating conflicts, keyed by catalogId. Presence
   * of a VALID confirmation is what lets a `confirmationRequired` entry be
   * applied — see `validateEquipmentRatingConfirmation`.
   */
  equipmentRatingConfirmations?: Record<string, GenerationEquipmentRatingConfirmation>;
  /** User-curated suggested-equipment shortlist + user-supplied entries. */
  equipmentWorkspace?: GenerationEquipmentWorkspace;
  /** Optional and added only after a catalog-bound OEM/EPC project dossier is applied. */
  projectDutyBasis?: GenerationProjectDutyBasis;
  /** Optional immutable provenance link to the active generation procurement BOM. */
  bomBasis?: GenerationBomBasis;
  layoutAnchor?: { lat: number; lng: number };
  /** Optional for backward compatibility with generation-impact-v1 saves. */
  layout?: GenerationLayoutConfiguration;
}

export type EngineeringVerdict = 'pass' | 'fail' | 'indeterminate';

export interface GenerationImpactRisk {
  id: 'n-1-shortfall' | 'reserve-shortfall' | 'contingency-concentration' | 'gas-capacity-mismatch' | 'gas-interconnect-derate' | 'water-source-mismatch' | 'ccs-export-uncertainty' | 'receptor-noise-exceedance' | 'missing-vendor-permit-inputs';
  severity: 'amber' | 'red';
  message: string;
}

export interface GenerationUnitResult {
  unitId: string;
  label: string;
  contingencyGroupId: string;
  count: number;
  siteGrossMWPerUnit: number;
  ccsLoadMWPerUnit: number;
  netMWPerUnit: number;
  totalNetMW: number;
  peakGasMmbtuPerHour: number;
}

export interface NoiseComponentResult {
  unitId: string;
  unitName: string;
  levelDbA: number | null;
  status: 'calculated' | 'inside-reference-distance' | 'missing-input';
  basis: 'sound-power' | 'reference-spl' | 'legacy-sound-power';
}

/** Workbench receptor result; optional detail fields preserve v1 snapshots. */
export interface NoiseReceptorResult {
  receptorId: string;
  label: string;
  predictedDbA: number | null;
  thresholdDbA?: number;
  verdict: EngineeringVerdict;
  caveat: string;
  distanceM?: number;
  /** Positive means the prediction is below the threshold. */
  marginDb?: number | null;
  components?: NoiseComponentResult[];
  modelBasis?: 'free-field-screening' | 'grid-screening';
}

export interface NoiseContourResult {
  levelDbA: 45 | 50 | 55 | 60 | 65;
  radiusM: number | null;
  status: 'calculated' | 'inside-reference-distance' | 'missing-input';
}

export const NOISE_FIELD_MODEL_VERSION = 'grid-screening-v2' as const;

/** Closed "≥ levelDbA" regions of the propagation field, outermost first. */
export interface NoiseFieldBand {
  /** Plain number by design — NOT NoiseContourResult's frozen literal union. */
  levelDbA: number;
  /** Closed rings (first point repeated last), lat/lng, 7-dp rounded. */
  rings: Array<Array<{ lat: number; lng: number }>>;
}

/**
 * Frozen grid-screening noise field. Derived purely from scenario input
 * (replay-safe); bands render as map layers, receptorLevels keep the
 * receptor table and the map in agreement by construction.
 */
export interface NoiseFieldResult {
  modelVersion: typeof NOISE_FIELD_MODEL_VERSION;
  /** Local-plane origin (equirectangular reference). */
  origin: { lat: number; lng: number };
  gridSpacingM: number;
  gridColumns: number;
  gridRows: number;
  bands: NoiseFieldBand[];
  receptorLevels: Array<{
    receptorId: string;
    predictedDbA: number | null;
    /** 'field-sample' = evaluated at receptor.location through barriers;
     *  'distance-screen' = v2 distance formula (no location available). */
    basis: 'field-sample' | 'distance-screen';
  }>;
  /** Resolved emitters actually used — reproducibility record. */
  sources: Array<{
    noiseSourceId: string;
    label: string;
    /** Equivalent per-emitter sound power, dBA re 1 pW (post scope/count split). */
    equivalentLwDbA: number;
    positions: Array<{ lat: number; lng: number }>;
    heightM: number;
  }>;
  assumptions: string[];
  caveats: string[];
}

/** Full generation-workbench result retained under its existing public name. */
export interface GenerationImpactResult {
  schemaVersion: typeof GENERATION_IMPACT_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_IMPACT_MODEL_VERSION;
  verdict: EngineeringVerdict;
  unitResults: GenerationUnitResult[];
  capacity: {
    normalNetMW: number;
    largestContingencyGroupId: string | null;
    largestContingencyMW: number;
    nMinusOneNetMW: number;
    criticalLoadMW: number;
    reserveRequiredMW: number;
    nMinusOneMarginMW: number;
    nMinusOneReserveMarginMW: number;
    nMinusOneVerdict: EngineeringVerdict;
    nMinusOneReserveVerdict: EngineeringVerdict;
    /** User-selected contingency criterion, retained alongside the N-1 baseline. */
    contingencyOutages: number;
    contingencyLostMW: number;
    contingencyNetMW: number;
    contingencyMarginMW: number;
    contingencyReserveMarginMW: number;
    contingencyVerdict: EngineeringVerdict;
    contingencyReserveVerdict: EngineeringVerdict;
  };
  reliability: {
    probabilityLoadServed: number | null;
    caveat: string;
  };
  gas: {
    peakMmbtuPerHour: number;
    peakDthPerDay: number;
    annualMmbtu: number;
  };
  emissions: {
    uncontrolledCo2TonnesPerYear: number;
    capturedCo2TonnesPerYear: number;
    residualCo2TonnesPerYear: number;
    noxTonnesPerYear: number | null;
    coTonnesPerYear: number | null;
  };
  water: {
    generationWithdrawalGalPerDay: number;
    generationConsumptionGalPerDay: number;
    ccsWithdrawalGalPerDay: number;
    ccsConsumptionGalPerDay: number;
    dataCenterWithdrawalGalPerDay: number | null;
    dataCenterConsumptionGalPerDay: number | null;
    totalWithdrawalGalPerDay: number | null;
    totalConsumptionGalPerDay: number | null;
    generationBasis?: 'gross-output-intensity' | 'absolute-design-day';
  };
  footprint: {
    plantAcres: number;
    coolingAcres: number;
    switchyardAcres: number;
    ccsAcres: number;
    compressionAcres: number;
    totalAcres: number;
    plantAreaBasis?: 'site-rated-gross-intensity' | 'absolute-installed-area';
    /** Present ONLY when the scenario declared `coolingAcresPerGrossMW`; absent
     *  on every legacy result, which is what keeps `resultHash` stable. */
    coolingAreaBasis?: 'gross-output-intensity';
    /** Same contract as `coolingAreaBasis`. */
    switchyardAreaBasis?: 'gross-output-intensity';
  };
  noise: NoiseReceptorResult[];
  /** Added detail is optional so previously frozen v1 results remain readable. */
  noiseContours?: NoiseContourResult[];
  noiseModelBasis?: 'free-field-screening' | 'grid-screening';
  noiseModelVersion?: 'free-field-screening-v1' | 'grid-screening-v2';
  noiseCaveats?: string[];
  /** Grid-screening propagation field; present only for v2-opted scenarios. */
  noiseField?: NoiseFieldResult;
  lighting: {
    installedPowerKW: number | null;
    installedLumens: number | null;
    planningFlags: string[];
  };
  gaps: string[];
  warnings: string[];
  risks: GenerationImpactRisk[];
}

export interface GenerationImpactScenarioSnapshot {
  id: string;
  siteId: string;
  name: string;
  targetPhaseId?: string;
  input: GenerationImpactScenarioInput;
  result: GenerationImpactResult;
  inputHash: string;
  resultHash: string;
  /** Workflow-only common-input binding captured on selection. Does not
   * change the historical scenario input/result hashes or physics model. */
  sharedProjectInputHash?: string;
  /**
   * Explicit v1 marks the legacy JSON.stringify convention. Markerless
   * snapshots may be canonical current saves or historic pre-marker records;
   * integrity checks require the complete input/result pair to match one
   * convention and reject mixed pairs.
   */
  hashVersion?: typeof GENERATION_IMPACT_HASH_VERSION;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  promotedAt?: string;
  promotedBy?: string;
  /** Read-time integrity annotation; never persisted as part of the scenario payload. */
  integrity?: {
    valid: boolean;
    errors: string[];
  };
}

export interface GenerationImpactExpectedScenario {
  updatedAt: string;
  inputHash: string;
  resultHash: string;
}

/** Site-level pointer to the one active, immutable layout publication. */
export interface GenerationImpactLayoutHead {
  schemaVersion: typeof GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION;
  version: number;
  publicationId: string;
  scenarioId: string;
  scenarioName: string;
  scenarioUpdatedAt: string;
  inputHash: string;
  resultHash: string;
  modelVersion: string;
  layoutHash: string;
  layerIds: string[];
  publishedAt: string;
  publishedBy: string;
}

export interface GenerationImpactLayoutChangeSummary {
  publishedLayerCount: number;
  retiredManagedLayerCount: number;
  preservedNonManagedLayerCount: number;
}

/** Full, immutable history row. The scenario and geometry are frozen in-place. */
export interface GenerationImpactLayoutPublication extends GenerationImpactLayoutHead {
  requestId: string;
  requestHash: string;
  previousPublicationId?: string;
  scenarioSnapshot: GenerationImpactScenarioSnapshot;
  layers: import('./site').SiteLayer[];
  changeSummary: GenerationImpactLayoutChangeSummary;
}

export interface GenerationImpactBoundaryPoint {
  lat: number;
  lng: number;
}

export type GenerationImpactBoundaryRing = GenerationImpactBoundaryPoint[];
export type GenerationImpactBoundaryPolygon = GenerationImpactBoundaryRing[];

/**
 * Canonical union of the parcel polygons used by one SED rebuild. Rings are
 * detached from mutable site state, non-overlapping, and stored without a
 * repeated closing vertex. The integrity hash covers the schema, source,
 * canonical geometry, and union area.
 */
export interface GenerationImpactParcelBoundarySnapshot {
  schemaVersion: typeof GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION;
  source: 'site-boundaries' | 'site-boundary';
  /** MultiPolygon coordinates: each polygon is [outer ring, ...interior holes]. */
  polygons: GenerationImpactBoundaryPolygon[];
  areaAcres: number;
  boundaryHash: string;
}

/**
 * Compact identity carried beside the full SED authority snapshot. Consumers
 * can compare this tuple without interpreting layout geometry or mutable site
 * state.
 */
export interface GenerationImpactSedProvenance {
  contractVersion: typeof GENERATION_IMPACT_SED_CONTRACT_VERSION;
  scenarioId: string;
  inputHash: string;
  resultHash: string;
  modelVersion: string;
  promotedAt?: string;
  layoutPublicationId: string;
  layoutVersion: number;
  layoutHash: string;
  layoutPublishedAt: string;
  parcelBoundarySchemaVersion: typeof GENERATION_IMPACT_BOUNDARY_SCHEMA_VERSION;
  parcelBoundarySource: GenerationImpactParcelBoundarySnapshot['source'];
  parcelBoundaryHash: string;
  parcelBoundaryAreaAcres: number;
}

/**
 * Authoritative Generation & Impact basis frozen into one SED revision.
 *
 * The immutable layout publication includes the exact generated geometry and
 * its own scenario snapshot. `scenarioSnapshot` is retained explicitly as the
 * promoted user decision, while `footprint` is projected in-place so Test Fit
 * never has to reconstruct acreage from mutable Project Intent fields.
 */
export interface GenerationImpactSedAuthoritySnapshot {
  contractVersion: typeof GENERATION_IMPACT_SED_CONTRACT_VERSION;
  capturedAt: string;
  provenance: GenerationImpactSedProvenance;
  scenarioSnapshot: GenerationImpactScenarioSnapshot;
  layoutPublication: GenerationImpactLayoutPublication;
  parcelBoundary: GenerationImpactParcelBoundarySnapshot;
  footprint: GenerationImpactFootprintProjection;
}

/**
 * Compact immutable authority tuple carried by an on-site Test Fit job and
 * frozen report. It intentionally contains identities only: full scenario,
 * impact, layout, and parcel geometry remain on the SED authority snapshot.
 *
 * Job runners compare every field immediately before execution and
 * persistence. A mismatch means the requested report authority went stale;
 * callers must never mix the old request with newly loaded evidence.
 */
export interface TestFitAuthorityRef {
  readonly scenarioId: string;
  readonly scenarioUpdatedAt: string;
  readonly inputHash: string;
  readonly resultHash: string;
  readonly modelVersion: string;
  readonly promotedAt: string;
  readonly layoutPublicationId: string;
  readonly layoutVersion: number;
  readonly layoutHash: string;
  readonly layoutPublishedAt: string;
  readonly sedId: string;
  readonly sedVersion: number;
  readonly boundaryHash: string;
}

export type GenerationImpactSedAuthorityResolution =
  | { status: 'absent'; snapshot: null }
  | { status: 'invalid'; snapshot: null; reason: string }
  | { status: 'valid'; snapshot: GenerationImpactSedAuthoritySnapshot };

/** Bounded history metadata returned to the workbench; large snapshots stay server-side. */
export type GenerationImpactLayoutPublicationSummary = Omit<
  GenerationImpactLayoutPublication,
  'requestHash' | 'scenarioSnapshot' | 'layers'
>;

export interface GenerationImpactLayoutWorkspaceState {
  active: GenerationImpactLayoutHead | null;
  recent: GenerationImpactLayoutPublicationSummary[];
  activeLayers: import('./site').SiteLayer[];
  /** True when pre-versioning scenario-derived layers still require canonical replacement. */
  legacyUnversioned: boolean;
}

export interface GenerationImpactReportSource {
  scenarioId: string;
  scenarioName: string;
  targetPhaseId?: string;
  promotedAt?: string;
  inputHash: string;
  resultHash: string;
  schemaVersion: number;
  modelVersion: string;
  /** Immutable published-layout identity frozen into the SED used for this report. */
  layoutPublicationId?: string;
  layoutVersion?: number;
  layoutHash?: string;
  layoutPublishedAt?: string;
  /** Version of the promoted-scenario/published-layout SED contract. */
  sedAuthorityContractVersion?: number;
  /** Exact canonical parcel boundary frozen into the same SED authority. */
  parcelBoundarySchemaVersion?: number;
  parcelBoundarySource?: GenerationImpactParcelBoundarySnapshot['source'];
  parcelBoundaryHash?: string;
  parcelBoundaryAreaAcres?: number;
  /** Public-safe aggregate proving which frozen procurement basis informed the scenario. */
  bomProvenance?: {
    basisHash: string;
    sourcePlanHash: string;
    sourcePlanVersion: number;
    itemCount: number;
    totalNetNameplateMW: number | null;
    capacityBasis: GenerationBomBasis['capacityBasis'];
    solverBinding: GenerationBomBasis['solverBinding'];
  };
}

/**
 * Joint capacity-screen verdict with the reserve-padded requirement treated
 * as binding. 'conditional' names the load-pass/reserve-fail state the
 * solver's headline verdict alone would overstate as a bare pass.
 */
export type GenerationCapacityScreenVerdict = 'pass' | 'conditional' | 'fail' | 'indeterminate';

/** Ambient × degradation basis a scenario was screened on (frozen inputs). */
export interface GenerationScreeningBasisUnitProjection {
  unitId: string;
  label: string;
  ambientFactor: number | null;
  degradationFactor: number | null;
}

export interface GenerationScreeningBasisProjection {
  /** e.g. "0.9 × 0.98", or "mixed (…)" when units differ within one scenario. */
  label: string;
  uniform: boolean;
  units: GenerationScreeningBasisUnitProjection[];
  note?: string;
}

/**
 * Basis label for the result's annual gas/CO2/water figures: they are
 * full-output capability for on-site/mixed fleets, or declared equivalent
 * grid-import duty for an all-grid supply envelope. Derived from frozen inputs only.
 */
export interface GenerationAnnualizationBasisProjection {
  basis: 'full-output-capability' | 'grid-import-duty';
  annualOperatingHours: number;
  screenedAnnualGWh: number | null;
  facilityLoadAnnualGWh: number | null;
  /** On-site capability share, or 1 for a usable declared grid-import duty; never scale imports twice. */
  facilityLoadShare: number | null;
  note: string;
}

/** PLS translated into hours against a data-center availability target. */
export interface GenerationReliabilityPerspectiveProjection {
  expectedUnservedHoursPerYear: number | null;
  tierShorthandHoursPerYear: number | null;
  note: string;
}

/** Peak-vs-served gas framing for interconnect and offtake conversations. */
export interface GenerationGasDemandPerspectiveProjection {
  peakDthPerDay: number;
  servedAverageDthPerDay: number | null;
  note: string;
}

/** One installed-cost class row of the indicative screening economics. */
export interface GenerationScreeningEconomicsRow {
  label: string;
  lowUsdPerKw: number;
  highUsdPerKw: number;
  servedUsdPerMwhLow: number | null;
  servedUsdPerMwhHigh: number | null;
  capabilityUsdPerMwhLow: number | null;
  capabilityUsdPerMwhHigh: number | null;
}

/**
 * Order-of-magnitude generation economics frozen onto a report at gen time.
 * Explicitly a screening basis: public planning classes + the SED fuel
 * price; never a pro forma (see the projection's `note`).
 */
export interface GenerationScreeningEconomicsProjection {
  basis: 'indicative-screening';
  fixedChargeRate: number;
  fuelPriceUsdPerMmbtu: number;
  fuelPriceSource: 'sed-fuel-supply' | 'unavailable';
  fuelUsdPerMwhNet: number;
  omUsdPerMwh: number;
  servedCapacityFactor: number | null;
  capabilityCapacityFactor: number | null;
  rows: GenerationScreeningEconomicsRow[];
  capture?: {
    creditUsdPerTonne: number;
    captureCostUsdPerTonneLow: number;
    captureCostUsdPerTonneHigh: number;
    captureAdderUsdPerKwLow: number;
    captureAdderUsdPerKwHigh: number;
    servedCaptureTonnesPerYear: number | null;
    servedCreditMUsdPerYear: number | null;
    note: string;
  };
  note: string;
}

export interface GenerationImpactCapacityResilienceProjection {
  /** All installed units are grid imports; this does not establish utility firm service. */
  capacityRole?: 'grid-supply';
  qualificationNote?: string;
  criterion: string;
  contingencyOutages: number;
  facilityLoadMW: number;
  criticalLoadMW: number;
  reserveMarginPct: number;
  reserveRequiredMW: number;
  normalNetMW: number;
  contingencyLostMW: number;
  contingencyNetMW: number;
  loadMarginMW: number;
  reserveMarginMW: number;
  loadVerdict: EngineeringVerdict;
  reserveVerdict: EngineeringVerdict;
  probabilityLoadServed: number | null;
  reliabilityCaveat: string;
  /** Joint screen verdict (reserve binding); absent on pre-existing projections. */
  screenVerdict?: GenerationCapacityScreenVerdict;
  screenStatement?: string;
  screeningBasis?: GenerationScreeningBasisProjection;
  annualization?: GenerationAnnualizationBasisProjection;
  reliabilityPerspective?: GenerationReliabilityPerspectiveProjection;
  gasDemand?: GenerationGasDemandPerspectiveProjection;
}

/**
 * P3.4 — the basis of the water DUTY, emitted deterministically so no prose has
 * to explain (or is allowed to reconcile) why the promoted scenario's gal/day
 * and the Water Resource screen band differ by construction: the duty is
 * modeled at the equipment / process boundary (generation-plant cooling per
 * MWh gross, CCS per tonne captured, data-center cooling at a planning-default
 * intensity); the screen band is reported site-boundary WUE per kWh of IT load.
 */
export interface GenerationImpactWaterBasis {
  boundary: 'equipment-and-process';
  generationWithdrawalGalPerMWhGross: number;
  generationConsumptionGalPerMWhGross: number;
  dataCenterConsumptionGalPerMWhFacility: number | null;
  ccsIncluded: boolean;
  /** Reader-facing sentence, identical on screen, print, DOCX, PPTX and audit text. */
  statement: string;
}

export interface GenerationImpactWaterProjection {
  /** Absent only on projections frozen before P3.4 (2026-09-05). */
  basis?: GenerationImpactWaterBasis;
  availableConsumptionGalPerDay: number | null;
  generationWithdrawalGalPerDay: number;
  generationConsumptionGalPerDay: number;
  ccsWithdrawalGalPerDay: number;
  ccsConsumptionGalPerDay: number;
  dataCenterWithdrawalGalPerDay: number | null;
  dataCenterConsumptionGalPerDay: number | null;
  totalWithdrawalGalPerDay: number | null;
  totalConsumptionGalPerDay: number | null;
  consumptionHeadroomGalPerDay: number | null;
  adequacy: EngineeringVerdict;
}

/**
 * Fuel-supply adequacy frozen from the promoted scenario at generation.
 *
 * Screens gas demand against the documented interconnect on TWO bounds: the
 * rated/contracted figure, and what the station physically passes at the
 * transporter's guaranteed minimum delivery pressure. `quantityBasis` keeps the
 * claim honest — an 'indicative' (scoping / ROM-class) rating is reported as
 * such and is never firm transport.
 *
 * Absent when the promoted scenario documents no interconnect, which is every
 * scenario created before the interconnect duty existed.
 */
export interface GenerationImpactFuelSupplyProjection {
  quantityBasis: 'firm-transport-agreement' | 'indicative';
  /** Calculated peak nomination from the frozen solver result. */
  peakGasDthPerDay: number;
  stationDthPerDay: number | null;
  contractDthPerDay: number | null;
  firmDeliverableDthPerDay: number | null;
  firmBindingLimit: 'station-design' | 'contract-mdq' | null;
  deratedDeliverableDthPerDay: number | null;
  deratedBindingLimit: 'station-design' | 'contract-mdq' | null;
  headroomAtRatedPressureDthPerDay: number | null;
  headroomAtMinimumPressureDthPerDay: number | null;
  ratedAtPressureKPa: number;
  ratedAtPressureReference: 'gauge' | 'absolute';
  /** Null when the transporter has not stated a guaranteed minimum. */
  documentedMinimumDeliveryPressureKPa: number | null;
  documentedMinimumDeliveryPressureReference: 'gauge' | 'absolute' | null;
  absolutePressureRatio: number | null;
  compressibilityBasis: 'documented' | 'ideal-gas-assumed';
  /** One-sided band on the derated figure; 0 when compressibility is documented. */
  uncertaintyDthPerDay: number;
  adequacy: EngineeringVerdict;
  assumptions: string[];
}

export interface GenerationImpactFootprintProjection {
  plantAcres: number;
  coolingAcres: number;
  switchyardAcres: number;
  ccsAcres: number;
  compressionAcres: number;
  totalAcres: number;
  /** Gross parcel/boundary acreage frozen at report generation. */
  parcelAreaAcres?: number | null;
  parcelAreaSource?:
    | 'sed-authority-boundary'
    | 'site-boundary-aggregate'
    | 'site-boundaries'
    | 'site-area'
    | 'sed-site-profile'
    | 'sed-parcel'
    | 'unavailable';
  /** Deterministic conservative planning-screen deductions. */
  wetlandAcres?: number | null;
  floodOverlapAcres?: number | null;
  setbackAllowanceAcres?: number | null;
  netBuildableAcres?: number | null;
  netBuildableBasis?: 'conservative-planning-screen' | 'partial-planning-screen' | 'unavailable';
  constraintDataComplete?: boolean;
  /** Net-buildable minus the complete Generation & Impact footprint. */
  footprintMarginAcres?: number | null;
  acreageFitVerdict?: EngineeringVerdict;
  /**
   * Generation-layout-only result. This does not assert that data halls,
   * roads, stormwater, setbacks, or the overall campus fit.
   */
  spatialFitVerdict?: EngineeringVerdict;
  spatialFitBasis?: 'published-layout-within-site-boundary' | 'unavailable';
  outsideBoundaryAcres?: number | null;
  /** Union of published physical polygons; overlap is totalAcres minus this union. */
  representedFootprintAcres?: number | null;
  overlappingFootprintAcres?: number | null;
  assessedLayoutRoles?: string[];
  spatialFitReason?: string;
  /**
   * Set when the frozen footprint intensity sits far outside the current
   * generation-island planning convention band (frozen figures still rule).
   */
  footprintConventionNote?: string;
}

export interface GenerationImpactEnvironmentalCommunityProjection {
  /** CO2 rows are the full-output capability basis; see `annualization`. */
  annualization?: GenerationAnnualizationBasisProjection;
  uncontrolledCo2TonnesPerYear: number;
  capturedCo2TonnesPerYear: number;
  residualCo2TonnesPerYear: number;
  noxTonnesPerYear: number | null;
  coTonnesPerYear: number | null;
  noise: Array<Omit<NoiseReceptorResult, 'distanceM'> & { distanceM: number | null }>;
  lighting: GenerationImpactResult['lighting'] & {
    fullyShielded: boolean | null;
    bugRated: boolean | null;
    motionOrCurfewControls: boolean | null;
    iesPhotometricAvailable: boolean | null;
  };
}

export interface GenerationImpactGoNoGoProjection {
  /** Capacity/critical-load screen only; never the overall site Go/No-Go verdict. */
  capacityVerdict: EngineeringVerdict;
  /** Joint screen verdict (reserve binding); absent on pre-existing projections. */
  capacityScreenVerdict?: GenerationCapacityScreenVerdict;
  criterion: string;
  loadMarginMW: number;
  reserveMarginMW: number;
  totalWaterConsumptionGalPerDay: number | null;
  footprintAcres: number;
  residualCo2TonnesPerYear: number;
  risks: GenerationImpactRisk[];
  gaps: string[];
  warnings: string[];
  summary: string;
}

/** Deterministic, purpose-specific projection frozen onto an applicable report. */
/**
 * Flattened, render-ready max-power projection. Deliberately NOT the ladder —
 * a report needs the answer, its binder and its caveats, not 200 evaluated rungs.
 */
export interface GenerationImpactMaxPowerProjection {
  modelVersion: string;
  /** How the figure must be read. Never 'exact'. */
  boundQuality:
    | 'bounded-by-documented-constraints'
    | 'upper-bound-undocumented-constraints'
    | 'search-ceiling-reached';
  /** Prefix a renderer must honour: '≥' when nothing bound the sweep. */
  qualifier: '' | '≥';
  firmNetMW: number;
  isoGrossNameplateMW: number;
  siteGrossMW: number;
  blockCount: number;
  isoGrossMWPerBlock: number;
  facilityMW: number;
  facilityMWAtGenerationTerminals: number;
  itLoadMW: number;
  apparentMVA: number;
  pue: number;
  pueSource: string;
  tdLossPct: number;
  bindingConstraintIds: string[];
  bindingStatement: string;
  landBasis: 'parcel-geometry' | 'unavailable';
  ceilings: Array<{
    id: GenerationMaxPowerConstraintId;
    label: string;
    status: GenerationMaxPowerCeilingStatus;
    ceilingSiteGrossMW: number | null;
    conservativeCeilingSiteGrossMW: number | null;
    limitValue: number | null;
    limitUnit: string | null;
    reason: string | null;
  }>;
  landFitNonMonotonic: boolean;
  /** Every assumption the study attached; a figure without these misleads. */
  caveats: string[];
}

export interface GenerationImpactReportProjection {
  reportType: import('../config/reportTypes').ReportTypeId;
  source: GenerationImpactReportSource;
  capacityAndResilience?: GenerationImpactCapacityResilienceProjection;
  waterResource?: GenerationImpactWaterProjection;
  fuelSupply?: GenerationImpactFuelSupplyProjection;
  testFitFootprint?: GenerationImpactFootprintProjection;
  environmentalAndCommunity?: GenerationImpactEnvironmentalCommunityProjection;
  goNoGoSummary?: GenerationImpactGoNoGoProjection;
  /** Indicative screening economics (feasibility / power-availability / executive). */
  screeningEconomics?: GenerationScreeningEconomicsProjection;
  /** Present only when a fresh max-power study is stamped for this site. */
  maxPowerEnvelope?: GenerationImpactMaxPowerProjection;
  /** Numeric envelope omitted until the source-role input is corrected and recomputed. */
  maxPowerWithheldReason?: string;
  /**
   * The candidate set the promoted scenario was chosen from — present only when
   * the engineer actually compared two or more.
   *
   * `source` stays SINGULAR and authoritative: exactly one scenario is the duty
   * basis, and every existing consumer reads it unchanged. This is the record of
   * METHOD beside it, so a report can state what was screened and rejected
   * rather than presenting the winner as the only thing ever considered.
   */
  comparison?: GenerationImpactComparisonProjection;
}

/** One row of the promoted-vs-rejected screening comparison. */
export interface GenerationImpactComparisonRow {
  scenarioId: string;
  scenarioName: string;
  /** True for the row that became the duty basis. Exactly one row is promoted. */
  promoted: boolean;
  /** N, N-1, N-2 … as configured on that scenario. */
  criterionLabel: string;
  criterionNetMW: number;
  loadMarginMW: number;
  reserveMarginMW: number;
  /** Vendor identity, or the planning technology when no envelope was applied. */
  equipmentBasis: string;
  peakGasMmbtuPerHour: number;
  /** Null when the data-centre cooling basis is undocumented. */
  peakWaterConsumptionGalPerDay: number | null;
  footprintTotalAcres: number;
  /** Frozen integrity stamps, so a row is traceable to its saved scenario. */
  inputHash: string;
  resultHash: string;
}

export interface GenerationImpactComparisonProjection {
  /** Promoted row first, then the rejected candidates in comparison order. */
  rows: GenerationImpactComparisonRow[];
  /** Candidates recorded on the intent that could no longer be read. */
  unresolvedScenarioIds: string[];
  /**
   * Plain-language statement of what the comparison establishes, written
   * deterministically from the rows — never a model claim.
   */
  statement: string;
}

/** Typed server response for the integrity-annotated scenario index. */
export interface ListGenerationImpactScenariosResult {
  scenarios: GenerationImpactScenarioSnapshot[];
  layout: GenerationImpactLayoutWorkspaceState;
}

export interface GenerationScenarioPreview {
  input: GenerationImpactScenarioInput;
  result: GenerationImpactResult;
  savedScenarioId?: string;
}

/** Compact, backward-compatible acoustic scenario frozen into generated reports. */
export interface GenerationNoiseUnit {
  id: string;
  name: string;
  count: number;
  noise?: NoiseDatum;
  /** Exact numerical fallback for scenarios persisted before NoiseDatum. */
  soundPowerDbA?: number;
  directivityCorrectionDb?: number;
  barrierLossDb?: number;
}

export interface GenerationNoiseReceptor {
  id: string;
  name: string;
  distanceM: number;
  barrierLossDb?: number;
  thresholdDbA?: number;
}

export interface GenerationNoiseScenario {
  id: string;
  name: string;
  units: GenerationNoiseUnit[];
  receptors: GenerationNoiseReceptor[];
  /** Optional common acoustic centre used by map renderers for radial contours. */
  center?: { lat: number; lng: number };
  notes?: string;
}

export interface FrozenNoiseReceptorResult {
  receptorId: string;
  receptorName: string;
  distanceM: number;
  predictedDbA: number | null;
  thresholdDbA: number | null;
  /** Positive means the prediction is below the threshold. */
  marginDb: number | null;
  verdict: 'pass' | 'exceedance' | 'indeterminate';
  components: NoiseComponentResult[];
}

export interface GenerationNoiseImpactResult {
  modelBasis: 'free-field-screening';
  modelVersion: 'free-field-screening-v1';
  receptors: FrozenNoiseReceptorResult[];
  contours: NoiseContourResult[];
  caveats: string[];
}

/**
 * Immutable-at-generation report payload. The promoted workbench snapshot is
 * preserved verbatim and paired with one deterministic acoustic projection.
 */
export interface GenerationImpactSnapshot {
  scenario: GenerationImpactScenarioSnapshot;
  noise: GenerationNoiseImpactResult;
  capturedAt: string;
}

export interface GenerationLayoutExhibitCadGeoreference {
  anchorLat: number;
  anchorLng: number;
  originX: number;
  originY: number;
  metersPerUnit: number;
  rotationDeg: number;
  source?: string;
  crsCode?: string;
  crsName?: string;
}

/** Allowlisted CAD identity retained on one frozen, editable overlay feature. */
export interface GenerationLayoutExhibitCadFeatureProvenance {
  sourceFile: string;
  /** Full SHA-256 from the imported DWG; never the shortened layer-id prefix. */
  sourceSha256: string;
  dwgVersion?: string;
  entityHandle?: string;
  entityType?: string;
  layer?: string;
  georeference?: GenerationLayoutExhibitCadGeoreference;
}

export interface GenerationLayoutExhibitOverlayFeature {
  id: string;
  name: string;
  category: import('./site').SiteLayerCategory;
  source: import('./site').SiteLayer['source'];
  geometry: {
    type: 'point' | 'line' | 'polygon';
    coordinates: Array<{ lat: number; lng: number }>;
  };
  color?: string;
  contentHash?: string;
  importedAt: string;
  updatedAt?: string;
  cad?: GenerationLayoutExhibitCadFeatureProvenance;
}

export interface GenerationLayoutExhibitCadSource {
  sourceFile: string;
  /** Full source digest, suitable for visible provenance and equality checks. */
  sourceSha256: string;
  dwgVersion?: string;
  featureCount: number;
  layerNames: string[];
  georeference?: GenerationLayoutExhibitCadGeoreference;
}

export interface GenerationLayoutExhibitFitComponent {
  id: string;
  label: string;
  kind: 'generation-block' | 'cooling' | 'switchyard' | 'ccs-capture' | 'ccs-compression' | 'natural-gas-route' | 'co2-export-route' | 'construction-laydown' | 'access-road' | 'road' | 'parking' | 'building' | 'data-center' | 'generator' | 'custom';
  blockNumber?: number;
  areaAcres: number;
  xM: number;
  yM: number;
  widthM: number;
  heightM: number;
  polygonM: Array<{ xM: number; yM: number }>;
  fitsWithinEnvelope: boolean;
  coordinates: Array<{ lat: number; lng: number }>;
  containedAcres: number | null;
  outsideAcres: number | null;
  parcelEdgeClearanceM: number | null;
  meetsParcelEdgeSetback: boolean | null;
  /** Missing on legacy exhibits created before installed reservation support. */
  dimensionBasis?: 'planning-acreage' | 'project-installed-dimensions' | 'project-route-reservation' | 'project-construction-reservation' | 'user-defined';
  lifecycle?: 'permanent' | 'route-reservation' | 'construction';
  /** Present only on exhibits frozen with exclusion zones applied. */
  exclusionOverlapAcres?: number | null;
}

export interface GenerationLayoutExhibitFit {
  /** Missing on legacy v1 exhibits; present on recomputable installed-reservation fits. */
  fitModelVersion?: 'generation-test-fit-v2' | 'generation-test-fit-v3' | 'generation-test-fit-v4';
  status: 'fits' | 'partial' | 'indeterminate';
  error: string | null;
  caveats: string[];
  layout: GenerationLayoutConfiguration;
  anchor: { lat: number; lng: number };
  parcel: {
    id: string;
    label?: string;
    areaAcres: number;
    perimeterKm: number;
    centroid: { lat: number; lng: number };
    intendedUse?: import('./siteBoundary').SiteBoundaryParcel['intendedUse'];
    polygon: Array<{ lat: number; lng: number }>;
    geometryHash: string;
  } | null;
  envelope: {
    center: { lat: number; lng: number };
    widthM: number;
    heightM: number;
    parcelPolygonM: Array<{ xM: number; yM: number }>;
  } | null;
  plan: {
    status: 'fit' | 'overflow';
    orientation: 'blocks-along-width' | 'blocks-along-height';
    usableEnvelope: { widthM: number; heightM: number; areaAcres: number };
    packingEnvelope: { widthM: number; heightM: number; areaAcres: number };
    requiredEnvelope: { widthM: number; heightM: number; areaAcres: number };
    totalComponentAreaAcres: number;
    componentAreaMarginAcres: number;
    componentAreaUtilizationPct: number;
    widthOverflowM: number;
    heightOverflowM: number;
    orientationEvaluations: Array<{
      orientation: 'blocks-along-width' | 'blocks-along-height';
      fits: boolean;
      requiredWidthM: number;
      requiredHeightM: number;
      widthOverflowM: number;
      heightOverflowM: number;
      boundingAreaAcres: number;
    }>;
    /** Present only on exhibits frozen with a packing scheme requested. */
    packingScheme?: 'compact' | 'road-rows';
    /** Present only on exhibits frozen with a packing scheme requested. */
    schemeEvaluations?: Array<{
      scheme: 'compact' | 'road-rows';
      orientation: 'blocks-along-width' | 'blocks-along-height';
      fits: boolean;
      requiredWidthM: number;
      requiredHeightM: number;
      widthOverflowM: number;
      heightOverflowM: number;
      boundingAreaAcres: number;
    }>;
    reservationSummary?: {
      permanentEquipmentAcres: number;
      routeReservationAcres: number;
      constructionLaydownAcres: number;
      totalReservedAcres: number;
    };
  } | null;
  components: GenerationLayoutExhibitFitComponent[];
  containedAcres: number | null;
  outsideAcres: number | null;
  containmentPct: number | null;
  minimumParcelEdgeClearanceM: number | null;
  parcelEdgeSetbackSatisfied: boolean | null;
  /** Present only on exhibits frozen with exclusion zones applied. */
  usable?: {
    parcelAcres: number;
    excludedAcres: number;
    usableAcres: number;
    femaStatus: GenerationLayoutExclusionSet['femaStatus'];
  };
  /** Present only on exhibits frozen with exclusion zones applied. */
  exclusionOverlapAcres?: number | null;
  /** Present only when the anchor sweep translated the packed group. */
  placementOffsetM?: { dxM: number; dyM: number };
}

export interface GenerationSizingStudyUniformEnvelope {
  blockCount: number;
  onlineBlocksAfterContingency: number;
  configuredGrossMWPerBlock: number;
  netDeliveryFactor: number;
  requiredLoadGrossMWPerBlock: number;
  requiredReserveGrossMWPerBlock: number;
  suggestedLoadGrossMWPerBlock: number;
  suggestedReserveGrossMWPerBlock: number;
  configuredMeetsLoad: boolean;
  configuredMeetsReserve: boolean;
  incrementMW: number;
}

/** One contingency granularity on the N-1 overbuild curve. */
export interface GenerationSizingStudyGranularityRow {
  blockCount: number;
  onlineBlocksAfterContingency: number;
  /** Exact ISO gross MW per block required to meet the basis after N-1. */
  requiredGrossMWPerBlock: number;
  /** Exact fleet ISO nameplate; falls monotonically with block count. */
  requiredInstalledGrossMW: number;
  /** Fleet ISO nameplate after rounding every block up to the procurement increment. */
  installedGrossMW: number;
  /** installedGrossMW - requiredInstalledGrossMW. */
  incrementRoundingPenaltyMW: number;
  /** Site-condition net MW after losing one block, procurement basis. */
  firmAfterContingencyMW: number;
  /** Exact installed nameplate relative to the finest granularity swept. */
  requiredInstalledRelativeToFinest: number;
}

/**
 * N-1 overbuild across contingency granularities. Overbuild scales with the
 * largest contingency, not the load, so a fleet of n blocks installs about
 * n / (n - 1) times the delivered requirement.
 */
export interface GenerationSizingStudyGranularityCurve {
  basis: 'critical-load' | 'reserve';
  basisRequirementMW: number;
  configuredBlockCount: number;
  netDeliveryFactor: number;
  incrementMW: number;
  rows: GenerationSizingStudyGranularityRow[];
}

/** Coordinate-free scalar projection of one exact solver run in the sizing study. */
export interface GenerationSizingStudyOption {
  id: string;
  label: string;
  basis: 'configured' | 'reserve-rounded';
  blockCount: number;
  onlineBlocksAfterContingency: number;
  isoGrossMWPerBlock: number;
  isoGrossNameplateMW: number;
  siteNetMWPerBlock: number;
  normalNetMW: number;
  nMinusOneNetMW: number;
  criticalLoadMW: number;
  reserveRequiredMW: number;
  nMinusOneMarginMW: number;
  nMinusOneReserveMarginMW: number;
  nMinusOneVerdict: EngineeringVerdict;
  nMinusOneReserveVerdict: EngineeringVerdict;
  probabilityLoadServed: number | null;
  peakGasDthPerDay: number;
  generationAndCcsConsumptionGalPerDay: number;
  campusConsumptionGalPerDay: number | null;
  capturedCo2TonnesPerYear: number;
  residualCo2TonnesPerYear: number;
  footprintAcres: number;
  ccsEnabled: boolean;
  inputHash: string;
  resultHash: string;
}

/**
 * Backward-compatible nested study frozen into new layout exhibits. It is
 * deliberately independent of the top-level exhibit version so legacy v1
 * reports without this field remain readable.
 */
export interface GenerationSizingStudySnapshot {
  schemaVersion: typeof GENERATION_SIZING_STUDY_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_SIZING_STUDY_MODEL_VERSION;
  status: 'available' | 'unavailable';
  reason: string | null;
  incrementMW: number;
  basis: {
    baselineInputHash: string;
    baselineResultHash: string;
    candidateBlockCounts: number[];
    selectionCriterion: 'lowest-iso-nameplate-passing-n-1-plus-reserve';
  };
  uniformSizing: GenerationSizingStudyUniformEnvelope | null;
  /**
   * Optional and additive: exhibits frozen before the granularity curve existed
   * omit it entirely and remain valid at this model version.
   */
  contingencyGranularity?: GenerationSizingStudyGranularityCurve | null;
  options: GenerationSizingStudyOption[];
  /** Capacity-only screen; not a preferred vendor or project configuration. */
  lowestNameplatePassingReserveOptionId: string | null;
  warnings: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// MAX-POWER ENVELOPE STUDY
//
// The mirror of the sizing study: that one asks "how SMALL can this be and still
// serve the load", this one asks "how LARGE can this site go before something
// binds". It lives OUTSIDE `GenerationImpactResult` on purpose — the result is
// byte-locked by the publish-gate replay, and a study that depends on parcels,
// layout and polygon clipping is the highest-drift-risk computation in the repo.
// ─────────────────────────────────────────────────────────────────────────────

export type GenerationMaxPowerConstraintId =
  | 'land-parcel-fit'
  // NOTE: the report-side scalar land budget (`netBuildableAcres` =
  // parcel − wetland − flood − a flat 10% setback allowance, testFitProjection.ts)
  // is deliberately NOT a constraint here. It is a different, cruder budget than
  // the geometric parcel fit, and reconciling the two needs SED wetland/flood
  // data the sweep does not receive. Adding the id without the data path would
  // ship a constraint nothing can ever emit.
  | 'gas-firm-capacity'
  | 'gas-interconnect-derate'
  | 'water-consumption'
  | 'co2-export'
  | 'receptor-noise';

/**
 * `not-scaling` and `not-applicable` exist so "undocumented" is NEVER silently
 * read as "unlimited" — see the header of `maxPowerCeilings.ts`.
 */
export type GenerationMaxPowerCeilingStatus =
  | 'bounded'
  | 'not-binding'
  | 'indeterminate'
  | 'not-applicable'
  | 'not-scaling';

export interface GenerationMaxPowerConstraintCeiling {
  id: GenerationMaxPowerConstraintId;
  label: string;
  status: GenerationMaxPowerCeilingStatus;
  basis: 'analytic-linear' | 'enumerated';
  limitValue: number | null;
  limitUnit: string | null;
  /** Consumption per site-gross MW, read off the exact base solve. */
  intensityPerGrossMW: number | null;
  /** Continuous ceiling. Non-null ONLY for a bounded analytic constraint. */
  ceilingSiteGrossMW: number | null;
  /** Optimism-adjusted twin (currently the ideal-gas derate band). */
  conservativeCeilingSiteGrossMW: number | null;
  /** Highest ladder rung at which THIS constraint alone still passed. */
  lastPassingRungIndex: number | null;
  /** Always non-null when `status !== 'bounded'`. */
  reason: string | null;
}

/** One evaluated capacity on the ladder. Every rung is a REAL solver run. */
export interface GenerationMaxPowerLadderRung {
  index: number;
  blockCount: number;
  isoGrossMWPerBlock: number;
  isoGrossNameplateMW: number;
  siteGrossMW: number;
  normalNetMW: number;
  /** capacity.contingencyNetMW — honours the scenario's own N-k criterion. */
  firmNetMW: number;
  /** firmNetMW ÷ (1 + reserveMarginPct/100), the load this rung can actually serve. */
  servableCriticalLoadMW: number;
  constraintVerdicts: Array<{ id: GenerationMaxPowerConstraintId; verdict: EngineeringVerdict }>;
  /** True only when EVERY documented constraint passes simultaneously. */
  feasible: boolean;
  landFit?: {
    status: 'fits' | 'partial' | 'indeterminate';
    blocksPlaced: number;
    blocksContained: number;
    containmentPct: number | null;
    outsideAcres: number | null;
    areaUtilizationPct: number | null;
    greenReservePct: number | null;
    usableAcres: number | null;
    totalFootprintAcres: number;
    /**
     * True when a land screen could not be COMPUTED — clipping failed, the
     * clearance was underivable, the parcel would not resolve. Categorically
     * different from "the fleet does not fit", and mapped to an INDETERMINATE
     * verdict so a missing input never masquerades as a land ceiling.
     */
    screeningUnavailable: boolean;
    /** Which screen could not be computed; present only when unavailable. */
    screeningUnavailableReason?: string;
    /**
     * WHERE the placement came from. `exhausted` is the only value that makes a
     * land failure a genuine CEILING — every other failing value means the plant
     * simply did not fit where it happened to sit.
     */
    placementSource: 'carried-forward' | 'scenario-layout' | 'optimised' | 'exhausted';
    /** How many distinct placements were tried for this rung. */
    placementsEvaluated: number;
    /**
     * The geometry-neutral parameter changes that made it fit — ready to apply
     * in the Test-Fit Studio. Only rotation / block aspect ratio are searched;
     * setbacks, component gaps and road widths are requirements, never relaxed.
     */
    optimisedPlacement?: Record<string, number>;
    /** The full layout the winning fit used, for Studio hand-off. */
    resolvedLayout?: GenerationLayoutConfiguration;
  };
  inputHash: string;
  resultHash: string;
}

export interface GenerationMaxPowerStudySnapshot {
  schemaVersion: typeof GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_MAX_POWER_STUDY_MODEL_VERSION;
  status: 'available' | 'unavailable';
  reason: string | null;
  /**
   * How the headline must be READ. Never the string 'exact' — the documented
   * constraint set is incomplete (no air-permit limit, no water WITHDRAWAL
   * limit, no interconnection capacity, no construction access), so the best
   * achievable label is `bounded-by-documented-constraints`.
   */
  boundQuality:
    | 'bounded-by-documented-constraints'
    | 'upper-bound-undocumented-constraints'
    | 'search-ceiling-reached'
    | null;
  basis: {
    baselineInputHash: string;
    baselineResultHash: string;
    axis: 'equipment-block-count' | 'uniform-block-rating';
    axisLabel: string;
    fixedBlockCount: number | null;
    ladderIncrementMW: number | null;
    ladderMinRung: number;
    ladderMaxRung: number;
    evaluatedRungCount: number;
    /** Non-null when the ladder was COARSENED to fit the evaluation budget. */
    incrementCoarsenedFromMW: number | null;
    contingencyOutages: number;
    reserveMarginPct: number;
    reserveMarginDefaulted: boolean;
    landBasis: 'parcel-geometry' | 'unavailable';
    layoutRotationDeg: number | null;
    parcelId: string | null;
  };
  ceilings: GenerationMaxPowerConstraintCeiling[];
  bindingConstraintIds: GenerationMaxPowerConstraintId[];
  bindingStatement: string;
  maximum: {
    rungIndex: number;
    blockCount: number;
    isoGrossMWPerBlock: number;
    isoGrossNameplateMW: number;
    siteGrossMW: number;
    normalNetMW: number;
    firmNetMW: number;
    servableCriticalLoadMW: number;
    /** Inverse power stack — how big a campus this supports. */
    facilityMW: number;
    facilityMWAtGenerationTerminals: number;
    itLoadMW: number;
    gridMW: number;
    apparentMVA: number;
    pue: number;
    pueSource: string;
    powerFactor: number;
    tdLossPct: number;
    inputHash: string;
    resultHash: string;
  } | null;
  ladder: GenerationMaxPowerLadderRung[];
  /** True when a rung ABOVE the first land failure still fits — the packer is
   *  not monotone, so the ceiling is "largest VERIFIED fit", not a supremum. */
  landFitNonMonotonic: boolean;
  /**
   * Geometry-neutral layout changes the winning capacity required, ready to
   * apply in the Test-Fit Studio. Present only when the study had to search for
   * a placement — i.e. the scenario's own layout did not fit that capacity.
   */
  recommendedPlacement?: {
    values: Record<string, number>;
    placementsEvaluated: number;
  };
  warnings: string[];
}

/** Compact engineering schedule projected from the exact frozen scenario result. */
export interface GenerationLayoutExhibitEngineeringSchedule {
  fleet: {
    technologyGroupCount: number;
    generationBlockCount: number;
    isoGrossNameplateMW: number;
    siteGrossMW: number;
  };
  capacity: GenerationImpactResult['capacity'];
  gas: GenerationImpactResult['gas'];
  water: GenerationImpactResult['water'];
  emissions: GenerationImpactResult['emissions'];
  footprint: GenerationImpactResult['footprint'];
  nearestReceptor: FrozenNoiseReceptorResult | null;
  lighting: GenerationImpactResult['lighting'];
  gaps: string[];
  warnings: string[];
  noiseCaveats: string[];
  /** Missing for generic/legacy scenarios without an explicit equipment selection. */
  equipmentBasis?: GenerationEquipmentExhibitBasis;
  /** Missing for legacy or catalog-only scenarios without a project OEM/EPC dossier. */
  projectDutyBasis?: GenerationProjectDutyExhibitBasis;
  /** Missing only on generation-layout-exhibit-v1 revisions created before the sizing study. */
  sizingStudy?: GenerationSizingStudySnapshot;
}

/**
 * Immutable generation test-fit exhibit stored only in the full report body.
 * Overlay geometry is all-or-nothing: if the explicit byte ceiling is crossed,
 * every visible layer id is listed under `omittedLayerIds` and no prefix is kept.
 */
export interface GenerationLayoutExhibitSnapshot {
  schemaVersion: typeof GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION;
  /** Version of the deterministic packing/projection algorithm, independent of the power model. */
  modelVersion: typeof GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION;
  capturedAt: string;
  /** SHA-256 of every semantic field in this object except contentHash itself. */
  contentHash: string;
  scenario: {
    id: string;
    name: string;
    inputHash: string;
    resultHash: string;
    hashVersion: typeof GENERATION_IMPACT_HASH_VERSION;
    modelVersion: GenerationImpactResult['modelVersion'];
    updatedAt: string;
  };
  engineering: GenerationLayoutExhibitEngineeringSchedule;
  provenance: {
    siteLayersRevision: number | null;
    siteLayersCanonicalVersion: 1 | null;
    sourceLayerCount: number;
    visibleLayerCount: number;
    selectedParcelId: string | null;
    selectedParcelHash: string | null;
    /**
     * Present when the exhibit was projected directly from the immutable SED
     * authority. Legacy exhibits remain valid without it.
     */
    frozenAuthority?: {
      contractVersion: typeof GENERATION_IMPACT_SED_CONTRACT_VERSION;
      scenarioId: string;
      scenarioUpdatedAt: string;
      inputHash: string;
      resultHash: string;
      modelVersion: string;
      promotedAt: string;
      layoutPublicationId: string;
      layoutVersion: number;
      layoutHash: string;
      layoutPublishedAt: string;
      boundaryHash: string;
      boundaryAreaAcres: number;
      /** Present on Test Fits generated under the hard authority contract. */
      sedId?: string;
      sedVersion?: number;
    };
  };
  fit: GenerationLayoutExhibitFit;
  overlays: {
    status: 'included' | 'omitted-too-large' | 'omitted-invalid';
    maxBytes: number;
    serializedBytes: number;
    sourceLayerIds: string[];
    includedLayerIds: string[];
    omittedLayerIds: string[];
    /** Subset of omittedLayerIds whose malformed geometry caused fail-closed omission. */
    invalidLayerIds: string[];
    totalVertices: number;
    features: GenerationLayoutExhibitOverlayFeature[];
    cadSources: GenerationLayoutExhibitCadSource[];
  };
  /**
   * Max-power envelope study, frozen alongside the exhibit.
   *
   * TOP-LEVEL ON PURPOSE — deliberately NOT inside `engineering`. That block is
   * validated by full RE-DERIVATION and a canonical-JSON byte compare, and any
   * one-bit drift SILENTLY DROPS the entire exhibit. This study depends on
   * parcels, layout and polygon clipping, so it is the highest-drift-risk
   * computation in the repo — the last thing that should sit behind a
   * re-derivation gate. As a top-level field it is covered automatically by
   * `contentHash` (computed generically over the whole object) and validated by
   * cross-field invariants only.
   *
   * Missing on every exhibit revision created before this study existed.
   */
  maxPowerStudy?: GenerationMaxPowerStudySnapshot;
  warnings: string[];
}

/**
 * Coordinate-free, immutable ledger used by the unauthenticated report
 * compositor. It is projected from `GenerationLayoutExhibitSnapshot` only
 * after the semantic document has passed the public sanitizer. Raw parcel,
 * component and CAD geometry deliberately have no representation here.
 */
export interface PublicGenerationLayoutExhibitProjection {
  schemaVersion: typeof GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION;
  modelVersion: typeof GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION;
  capturedAt: string;
  /** Hash of the complete private exhibit that this public ledger describes. */
  contentHash: string;
  scenario: {
    id: string;
    name: string;
    inputHash: string;
    resultHash: string;
    hashVersion: typeof GENERATION_IMPACT_HASH_VERSION;
    modelVersion: GenerationImpactResult['modelVersion'];
    updatedAt: string;
  };
  /** Coordinate-free identity of the frozen scenario/layout/boundary tuple. */
  authority?: NonNullable<GenerationLayoutExhibitSnapshot['provenance']['frozenAuthority']>;
  engineering: {
    fleet: GenerationLayoutExhibitEngineeringSchedule['fleet'];
    capacity: GenerationImpactResult['capacity'];
    gas: GenerationImpactResult['gas'];
    water: GenerationImpactResult['water'];
    emissions: GenerationImpactResult['emissions'];
    footprint: GenerationImpactResult['footprint'];
    nearestReceptor: Omit<FrozenNoiseReceptorResult, 'components'> | null;
    lighting: GenerationImpactResult['lighting'];
    gaps: string[];
    warnings: string[];
    noiseCaveats: string[];
    /** Explicit null means the optional study was not evaluated for this revision. */
    equipmentBasis: GenerationEquipmentExhibitBasis | null;
    /** Explicit null means no frozen OEM/EPC duty dossier was evaluated. */
    projectDutyBasis: PublicGenerationProjectDutyExhibitBasis | null;
    /** Explicit null means no sizing-alternatives study was evaluated. */
    sizingStudy: GenerationSizingStudySnapshot | null;
  };
  fit: {
    fitModelVersion?: GenerationLayoutExhibitFit['fitModelVersion'];
    status: GenerationLayoutExhibitFit['status'];
    containedAcres: number | null;
    outsideAcres: number | null;
    containmentPct: number | null;
    minimumParcelEdgeClearanceM: number | null;
    parcelEdgeSetbackSatisfied: boolean | null;
    selectedParcelId: string | null;
    requiredEnvelopeWidthM?: number;
    requiredEnvelopeHeightM?: number;
    requiredEnvelopeAcres?: number;
    reservationSummary?: NonNullable<GenerationLayoutExhibitFit['plan']>['reservationSummary'];
  };
  overlays: {
    status: GenerationLayoutExhibitSnapshot['overlays']['status'];
    sourceLayerCount: number;
    visibleLayerCount: number;
    includedLayerCount: number;
    omittedLayerCount: number;
    invalidLayerCount: number;
    totalVertices: number;
    cadSources: Array<Pick<
      GenerationLayoutExhibitCadSource,
      'sourceFile' | 'sourceSha256' | 'dwgVersion' | 'featureCount' | 'layerNames'
    >>;
  };
  warnings: string[];
}

export interface GenerationImpactSiteInput { siteId: string; ownerUid?: string }
export interface SolveGenerationImpactInput extends GenerationImpactSiteInput { input: GenerationImpactScenarioInput }
export interface SaveGenerationImpactScenarioInput extends SolveGenerationImpactInput { scenarioId?: string; expectedUpdatedAt?: string }
export interface DeleteGenerationImpactScenarioInput extends GenerationImpactSiteInput { scenarioId: string; expectedUpdatedAt?: string }
export interface PromoteGenerationImpactScenarioInput extends GenerationImpactSiteInput {
  scenarioId: string;
  expectedUpdatedAt?: string;
  /** Exact current Intent/active-selection CAS for user-triggered promotion. */
  expectedDecisionHash?: string;
  /**
   * The candidate set this promotion was chosen from, in comparison order.
   * Evidence of method only — the server never treats a compared id as an
   * authority, and unknown ids are dropped rather than trusted.
   */
  comparedScenarioIds?: string[];
}
/**
 * Stamp the max-power envelope onto Project Intent.
 *
 * Note there is NO study body on the wire: the server RE-RUNS the study from
 * `scenario.input` and the site's own layout/parcel authority. A client-computed
 * body is never persisted — the same doctrine `assertScenarioReplayIntegrity`
 * enforces for scenario results.
 */
export interface StampMaxPowerStudyInput extends GenerationImpactSiteInput {
  scenarioId: string;
  expectedUpdatedAt?: string;
}
export interface StampMaxPowerStudyOutput {
  projectIntent: import('./siteRag').ProjectIntent;
  study: GenerationMaxPowerStudySnapshot;
  stampedAt: string;
}
export interface PromoteGenerationImpactScenarioOutput {
  projectIntent: import('./siteRag').ProjectIntent;
  promotedScenario: GenerationImpactScenarioSnapshot;
  promotedScenarioId: string;
  promotedAt: string;
  /** Promotion never starts an SED rebuild; geometry must be published first. */
  sedRebuildRequired: false;
  /** The exact promoted revision must be published before rebuilding the SED. */
  layoutPublishRequired: true;
}
export interface PublishGenerationImpactLayoutInput extends GenerationImpactSiteInput {
  scenarioId: string;
  requestId: string;
  expectedLayoutVersion: number;
  expectedScenario: GenerationImpactExpectedScenario;
}

export interface PublishGenerationImpactLayoutOutput {
  outcome: 'published' | 'already-published';
  publication: GenerationImpactLayoutPublicationSummary;
  head: GenerationImpactLayoutHead;
  layers: import('./site').SiteLayer[];
  /** Complete canonical layer set at the committed revision. */
  canonicalLayers: import('./site').SiteLayer[];
  /** Shared map-layer optimistic-concurrency revision. */
  revision: number;
  retiredLayerIds: string[];
  changeSummary: GenerationImpactLayoutChangeSummary;
  layout: GenerationImpactLayoutWorkspaceState;
  /** True only when this call committed a new active layout version. */
  sedRebuildRequired: boolean;
}
